import { announceStatus, debounce, DialogElement, Drawer, throttle } from "theme";
import { Delegate, timeline } from "vendor";
import { applyOowPatches } from "./oow-patch-registry.js";

const OOW_READY_EVENT = "oow:ready";

/** One full-width store detail, placed after the last card in the selected row. */
class OowStoreGroup extends HTMLElement {
  connectedCallback() {
    if (isMasterKilled() || getFeatures().stores?.storeGroup?.enabled === false) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.cards = Array.from(this.querySelectorAll('[data-store-card]'));
    this.activeCard = null;
    this.querySelectorAll('[data-store-detail]').forEach((detail) => { detail.hidden = true; });
    this.querySelectorAll('[data-store-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-store-close]')) {
        this.close();
        return;
      }

      const trigger = event.target.closest('[data-store-trigger]');
      if (!trigger) return;
      const card = trigger.closest('[data-store-card]');
      card === this.activeCard ? this.close() : this.show(card);
    }, { signal });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.activeCard) {
        event.preventDefault();
        this.close();
      }
    }, { signal });

    this.addEventListener('shopify:block:select', (event) => {
      const card = this.cards.find((item) => item.dataset.storeId === event.detail.blockId);
      if (card) this.show(card, false);
    }, { signal });

    this.resizeObserver = new ResizeObserver(() => {
      const width = this.getBoundingClientRect().width;
      if (width === this.lastWidth) return;
      this.lastWidth = width;
      this.positionDetail();
    });
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.scrollFrame);
  }

  show(card, correctScroll = true) {
    cancelAnimationFrame(this.scrollFrame);
    const previousCard = this.activeCard;
    const trigger = card.querySelector('[data-store-trigger]');
    const detail = this.querySelector(`#${CSS.escape(trigger.getAttribute('aria-controls'))}`);
    const rect = card.getBoundingClientRect();
    const wasVisible = rect.bottom > this.stickyTop() && rect.top < window.innerHeight;

    if (previousCard) {
      const previousTrigger = previousCard.querySelector('[data-store-trigger]');
      previousTrigger.setAttribute('aria-expanded', 'false');
      if (this.activeDetail.contains(document.activeElement)) trigger.focus({ preventScroll: true });
      this.activeDetail.hidden = true;
    }

    this.activeCard = card;
    this.activeDetail = detail;
    this.positionDetail();
    detail.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    announceStatus(trigger.dataset.openMessage);

    // Removing a tall earlier detail can otherwise leave the clicked row above the viewport.
    if (correctScroll && previousCard && wasVisible) {
      this.scrollFrame = requestAnimationFrame(() => {
        const currentRect = card.getBoundingClientRect();
        const top = this.stickyTop();
        if (currentRect.bottom <= top || currentRect.top >= window.innerHeight) {
          window.scrollBy({ top: currentRect.top - top - 16, behavior: 'instant' });
        }
      });
    }
  }

  positionDetail() {
    if (!this.activeCard) return;
    const columns = parseInt(getComputedStyle(this).getPropertyValue('--oow-store-columns'), 10);
    const index = this.cards.indexOf(this.activeCard);
    const rowEnd = Math.min(Math.floor(index / columns) * columns + columns, this.cards.length) - 1;
    const lastCard = this.cards[rowEnd];
    if (this.activeDetail.previousElementSibling !== lastCard) lastCard.after(this.activeDetail);
  }

  stickyTop() {
    // Resolve the theme's calc() custom property through a computed length.
    return parseFloat(getComputedStyle(this).scrollMarginBlockStart) || 0;
  }

  close() {
    if (!this.activeCard) return;
    cancelAnimationFrame(this.scrollFrame);
    const trigger = this.activeCard.querySelector('[data-store-trigger]');
    if (this.activeDetail.contains(document.activeElement)) trigger.focus({ preventScroll: true });
    this.activeDetail.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    this.activeCard = null;
    this.activeDetail = null;
    announceStatus(trigger.dataset.closeMessage);
  }
}

if (!isMasterKilled() && getFeatures().stores?.storeGroup?.enabled !== false && !window.customElements.get('oow-store-group')) {
  window.customElements.define('oow-store-group', OowStoreGroup);
}

class OowProductContent extends Drawer {
  connectedCallback() {
    super.connectedCallback();
    this.contentBreakpoint = window.matchMedia('(min-width: 700px)');
    this.contentBreakpoint.addEventListener('change', () => {
      if (this.open) this.hide();
    }, { signal: this.abortController.signal });
  }

  createEnterAnimationControls() {
    this.desktopContent = this.contentBreakpoint.matches;
    this.setAttribute('data-content-mode', this.desktopContent ? 'drawer' : 'modal');
    const title = this.querySelector('[slot="header"]');
    title?.classList.toggle('h4', this.desktopContent);
    title?.classList.toggle('h5', !this.desktopContent);
    this.getShadowPartByName('content').style.removeProperty('margin-inline-start');
    return this.desktopContent ? super.createEnterAnimationControls() : this.mobileAnimation(true);
  }

  createLeaveAnimationControls() {
    return this.desktopContent ? super.createLeaveAnimationControls() : this.mobileAnimation(false);
  }

  mobileAnimation(entering) {
    const options = { duration: 0.3, easing: [0.645, 0.045, 0.355, 1] };
    return timeline([
      [this.getShadowPartByName('overlay'), { opacity: entering ? [0, 1] : [1, 0] }, options],
      [this.getShadowPartByName('content'), { transform: entering ? ['translateY(100%)', 'translateY(0)'] : ['translateY(0)', 'translateY(100%)'] }, { ...options, at: '<' }]
    ]);
  }
}

if (!isMasterKilled() && !window.customElements.get('oow-product-content')) {
  window.customElements.define('oow-product-content', OowProductContent);
}

/**
 * Loox renders its reviews widget while the dialog is still closed, where the dialog host is display:none. Nudge the
 * widget once after the first open so it re-measures against the real dialog width.
 */
class OowProductReviews extends OowProductContent {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('dialog:after-show', () => this.syncReviewsLayout(), { signal: this.abortController.signal });
  }

  syncReviewsLayout() {
    if (this.reviewsLayoutSynced) {
      return;
    }

    this.reviewsLayoutSynced = true;
    window.dispatchEvent(new Event('resize'));
  }
}

if (!isMasterKilled() && !window.customElements.get('oow-product-reviews')) {
  window.customElements.define('oow-product-reviews', OowProductReviews);
}

/**
 * Size guide dialog opened from the variant picker size row.
 *
 * The Product Measurements table merges two sources: the rows of the matched `size_guide_profile`
 * metaobject (EU / UK / US and other size conversions) and the measurement rows of the first table
 * found in the product description. Sizes are the table header, so the profile `columns` label the
 * size columns while both sources contribute the measurement rows.
 */
class OowSizeGuide extends OowProductContent {
  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.abortController;

    this.unit = 'inch';
    this.parsed = false;

    this.addEventListener('dialog:before-show', () => this.renderGuide(), { signal });
    this.addEventListener('click', (event) => this.onClick(event), { signal });
  }

  onClick(event) {
    const unitButton = event.target.closest('[data-size-guide-unit]');

    if (unitButton) {
      this.setUnit(unitButton.dataset.sizeGuideUnit);
      return;
    }

    if (event.target.closest('[data-size-guide-close]')) {
      this.hide();
    }
  }

  renderGuide() {
    this.resetAccordions();

    if (!this.parsed) {
      this.parsed = true;
      this.profile = this.parseProfile();
      this.source = this.parseDescriptionTable();
      this.renderBodyMeasurements();
    }

    this.renderTable();
  }

  /**
   * The dialog always reopens with Product Measurements expanded and the other groups collapsed.
   * The theme collapse animation leaves inline height/overflow/opacity behind, so those are cleared as
   * well: a group collapsed earlier would otherwise reopen with an invisible panel.
   */
  resetAccordions() {
    this.querySelectorAll('[data-size-guide-accordion]').forEach((section) => {
      const details = section.querySelector('details');

      if (!details) {
        return;
      }

      const open = section.dataset.sizeGuideAccordion === 'product';
      details.open = open;
      details.setAttribute('aria-expanded', String(open));
      details.style.removeProperty('height');
      details.style.removeProperty('overflow');
      details.lastElementChild?.style.removeProperty('opacity');
    });
  }

  parseProfile() {
    const script = this.querySelector('[data-size-guide-profile]');

    if (!script) {
      return null;
    }

    try {
      const value = JSON.parse(script.textContent);
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  /** Reads the first table of the product description into plain rows, independent from the live DOM. */
  parseDescriptionTable() {
    const table = this.querySelector('[data-size-guide-table-source]')?.content.querySelector('table');

    if (!table) {
      return null;
    }

    const rows = Array.from(table.rows).map((row) => Array.from(row.cells).map((cell) => cell.textContent.trim()));
    const [header, ...body] = rows;

    if (!header || header.length < 2 || body.length === 0) {
      return null;
    }

    return { header, rows: body.filter((cells) => cells.length > 0) };
  }

  /**
   * Resolves the rendered size rows.
   *
   * The size labels come from the metaobject `columns.values`, because a profile label can carry extra
   * information the product description header does not have (shoes use `230 (36#)` for size `36`).
   * The product description header decides which of those sizes the product actually has: a size is kept
   * only when it is listed in `match` and one of the column labels carries it.
   */
  resolveColumns() {
    const profileValues = Array.isArray(this.profile?.columns?.values) ? this.profile.columns.values.map(String) : [];
    const match = Array.isArray(this.profile?.match) ? this.profile.match.map((value) => String(value).trim()) : [];
    const isMatched = (size) => match.length === 0 || match.includes(size);

    if (this.source) {
      return this.source.header.reduce((columns, size, index) => {
        if (index > 0 && isMatched(size)) {
          const profileIndex = profileValues.findIndex((label) => this.carriesSize(label, size));

          columns.push({
            label: profileIndex > -1 ? profileValues[profileIndex] : size,
            sourceIndex: index,
            profileIndex
          });
        }

        return columns;
      }, []);
    }

    return profileValues.reduce((columns, label, index) => {
      if (match.length === 0 || match.some((size) => this.carriesSize(label, size))) {
        columns.push({ label, sourceIndex: -1, profileIndex: index });
      }

      return columns;
    }, []);
  }

  /** A profile label carries a size when it equals it or lists it as one of its own tokens. */
  carriesSize(label, size) {
    const normalizedLabel = String(label).trim().toLowerCase();
    const normalizedSize = String(size).trim().toLowerCase();

    return normalizedLabel === normalizedSize || normalizedLabel.split(/[^0-9a-z]+/).includes(normalizedSize);
  }

  buildTable() {
    const columns = this.resolveColumns();

    // The profile and the product sizes can fail to line up (`29-34` in the description against a
    // `XS-XXXL` profile), in which case no column carries a profile value. Rendering the merge would
    // drop the whole table, so the product description table is used on its own instead.
    if (columns.length === 0 || columns.every((column) => column.profileIndex === -1)) {
      return this.buildDescriptionTable();
    }

    const profileRows = (Array.isArray(this.profile?.rows) ? this.profile.rows : []).filter((row) => row && row.visible !== false);
    const measurementRows = this.source ? this.source.rows : [];
    const header = [this.profile?.columns?.label || this.source?.header?.[0] || 'Size'];
    const body = [];

    columns.forEach((column) => header.push(column.label));

    profileRows.forEach((row) => {
      body.push([{ value: row.label }, ...columns.map((column) => ({ value: row.values?.[column.profileIndex] }))]);
    });

    measurementRows.forEach((row) => {
      body.push([{ value: row[0] }, ...columns.map((column) => ({ value: row[column.sourceIndex], measurement: true }))]);
    });

    return { header, body, hasMeasurement: measurementRows.length > 0 };
  }

  /** Fallback table built from the product description alone, without any profile conversion row. */
  buildDescriptionTable() {
    if (!this.source) {
      return null;
    }

    const sizes = this.source.header.slice(1);
    const header = [this.source.header[0] || 'Size', ...sizes];
    const body = this.source.rows.map((row) => [
      { value: row[0] },
      ...sizes.map((_, index) => ({ value: row[index + 1], measurement: true }))
    ]);

    return { header, body, hasMeasurement: true };
  }

  renderTable() {
    const container = this.querySelector('[data-size-guide-table]');

    if (!container) {
      return;
    }

    const model = this.buildTable();
    const switchElement = this.querySelector('.oow-size-guide__unit-switch');

    if (switchElement) {
      switchElement.hidden = !model?.hasMeasurement;
    }

    this.syncUnitButtons();

    if (!model) {
      container.replaceChildren();
      container.removeAttribute('tabindex');
      return;
    }

    const table = document.createElement('table');
    table.className = 'oow-size-guide__table';

    const headRow = table.createTHead().insertRow();

    model.header.forEach((label) => {
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = label ?? '';
      headRow.append(cell);
    });

    const tableBody = table.createTBody();

    model.body.forEach((cells) => {
      const row = tableBody.insertRow();

      cells.forEach((cell, index) => {
        const element = document.createElement(index === 0 ? 'th' : 'td');

        if (index === 0) {
          element.scope = 'row';
        }

        element.textContent = this.formatCell(cell);
        row.append(element);
      });
    });

    container.replaceChildren(table);
    container.setAttribute('tabindex', '0');
  }

  /**
   * Only the product description measurements are converted; profile conversions stay untouched.
   * A measurement can be a range (`22.3-22.7`); as in the legacy size guide, only its leading value is
   * converted, so the range collapses to a single inch value (`8.8`). Values carrying a slash are size
   * labels rather than measurements (`170/88A`) and are left untouched.
   */
  formatCell(cell) {
    const value = cell.value == null ? '' : String(cell.value);

    if (!cell.measurement || this.unit !== 'inch' || value.includes('/')) {
      return value;
    }

    const measurement = Number.parseFloat(value);
    return Number.isFinite(measurement) ? (measurement / 2.54).toFixed(1) : value;
  }

  setUnit(unit) {
    if (unit !== 'cm' && unit !== 'inch') {
      return;
    }

    this.unit = unit;
    this.renderTable();
  }

  syncUnitButtons() {
    this.querySelectorAll('[data-size-guide-unit]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.sizeGuideUnit === this.unit));
    });
  }

  /** Reads the Translation Lab language code; the measurement images exist per two letter locale. */
  resolveLanguageCode() {
    let stored = '';

    try {
      stored = localStorage.getItem('translation-lab-lang') || '';
    } catch {
      stored = '';
    }

    const code = stored.trim().toLowerCase().split('-')[0];
    return /^[a-z]{2}$/.test(code) ? code : 'en';
  }

  renderBodyMeasurements() {
    const container = this.querySelector('[data-size-guide-body]');

    if (!container) {
      return;
    }

    const sources = {
      men: 'man_size_',
      women: 'size-guide-',
      shoes: 'shoes_size_'
    };
    const source = sources[String(this.dataset.sizeGuideCategory || '').trim().toLowerCase()];

    if (!source) {
      container.replaceChildren(
        this.createText('p', 'oow-size-guide__empty', this.dataset.sizeGuideUnavailableTitle),
        this.createText('p', 'oow-size-guide__empty', this.dataset.sizeGuideUnavailableMessage),
        this.createText('button', 'button oow-size-guide__empty-cta', this.dataset.sizeGuideGotIt, {
          type: 'button',
          'data-size-guide-close': ''
        })
      );
      return;
    }

    const image = document.createElement('img');

    image.className = 'oow-size-guide__body-image';
    image.src = `https://cdn.shopify.com/s/files/1/0504/7644/9942/files/${source}${this.resolveLanguageCode()}.jpg`;
    image.alt = this.dataset.sizeGuideBodyLabel || '';
    image.loading = 'lazy';

    container.replaceChildren(image);
  }

  createText(tag, className, text, attributes = {}) {
    const element = document.createElement(tag);

    element.className = className;
    element.textContent = text ?? '';

    Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));

    return element;
  }
}

if (!isMasterKilled() && !window.customElements.get('oow-size-guide')) {
  window.customElements.define('oow-size-guide', OowSizeGuide);
}

const STORE_STOCK_MAX_CODES = 100;

class OowProductStoreStock extends OowProductContent {
  connectedCallback() {
    super.connectedCallback();
    this.productData ||= JSON.parse(this.querySelector('[data-store-stock-product]').textContent);
    this.stockCache ||= new Map();
    const records = this.querySelector('[data-store-records]');
    if (!this.stores) {
      this.stores = Array.from(records.content.children, (node) => node.cloneNode(true));
      this.nextStoresUrl = records.dataset.nextUrl;
    }
    const options = { signal: this.abortController.signal };
    if (/^Win/i.test(navigator.userAgentData?.platform || navigator.platform)) {
      this.querySelector('[data-store-size-options]').addEventListener('wheel', (event) => {
        this.scrollSizes(event);
      }, { ...options, passive: false });
    }
    this.addEventListener('dialog:before-show', () => {
      this.renderStores();
      this.syncSizes();
      this.loadRemainingStores();
    }, options);
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-store-variant]');
      if (!button) return;
      this.selectedStoreVariant = button.dataset.storeVariant;
      this.querySelectorAll('[data-store-variant]').forEach((item) => {
        item.setAttribute('aria-pressed', String(item === button));
      });
      this.syncStock();
    }, options);
    document.addEventListener('variant:change', (event) => {
      if (event.detail.formId === this.dataset.formId) this.syncSizes();
    }, options);
    document.addEventListener('product:rerender', (event) => {
      if (event.target.id !== this.dataset.formId) return;
      // This block is not in Prestige's partial-rerender list. Update its data without patching that list.
      const replacement = Array.from(event.detail.htmlFragment.querySelectorAll('oow-product-store-stock'))
        .find((element) => element.dataset.blockId === this.dataset.blockId);
      if (replacement && !event.detail.productChange) {
        this.productData = JSON.parse(replacement.querySelector('[data-store-stock-product]').textContent);
        queueMicrotask(() => this.syncSizes());
      }
      if (event.detail.productChange && this.open) this.hide();
    }, { ...options, capture: true });
  }

  scrollSizes(event) {
    // On Windows, keep wheel scrolling inside the size row, including at its edges. Preserve zoom gestures.
    if (!event.cancelable || event.defaultPrevented || event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    const container = event.currentTarget;
    if (container.scrollWidth <= container.clientWidth) return;
    const style = getComputedStyle(container);
    const unit = event.deltaMode === WheelEvent.DOM_DELTA_LINE
      ? parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2
      : event.deltaMode === WheelEvent.DOM_DELTA_PAGE ? container.clientWidth : 1;
    container.scrollLeft += (event.deltaX || event.deltaY * (style.direction === 'rtl' ? -1 : 1)) * unit;
  }

  syncSizes() {
    const data = this.productData;
    const form = document.getElementById(this.dataset.formId);
    const picker = Array.from(document.querySelectorAll('variant-picker'))
      .find((element) => element.getAttribute('form-id') === this.dataset.formId);
    const current = picker?.selectedVariant?.options ? picker.selectedVariant : data.selected;
    const variants = data.variants.filter((variant) => variant.options.every((value, index) =>
      index === data.sizeIndex || value === current.options[index]));
    const selectedInput = Array.from(form?.elements || []).find((input) => input.matches('input[data-size-label]:checked'));
    const selected = selectedInput?.dataset.variantId;
    const ordered = (data.sizes || []).flatMap((size) => variants.filter((variant) => variant.options[data.sizeIndex] === size));
    this.selectedStoreVariant = String(ordered.find((variant) => String(variant.id) === selected)?.id || ordered[0]?.id || current.id || '');
    const container = this.querySelector('[data-store-size-options]');
    container.replaceChildren(...ordered.map((variant) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.storeVariant = String(variant.id);
      button.textContent = variant.options[data.sizeIndex];
      button.setAttribute('aria-pressed', String(String(variant.id) === this.selectedStoreVariant));
      return button;
    }));
    this.querySelector('[data-store-sizes]').hidden = data.sizeIndex < 0 || !ordered.length;
    this.syncStock();
  }

  renderStores() {
    const country = getSourceDomain();
    const heading = this.querySelector('[data-store-local-heading]');
    heading.textContent = heading.dataset.heading.replace('__COUNTRY__', country);
    const local = this.querySelector('[data-store-local]');
    const other = this.querySelector('[data-store-other]');
    local.replaceChildren();
    other.replaceChildren();
    const order = (store) => {
      const value = store.dataset.sortOrder.trim();
      return value && Number.isFinite(Number(value)) ? Number(value) : Infinity;
    };
    this.stores.sort((a, b) => order(a) - order(b)).forEach((store) => {
      (this.isLocalStore(store) ? local : other).append(store);
    });
    this.querySelector('[data-store-empty-local]').hidden = local.childElementCount > 0;
    this.querySelector('[data-store-empty-other]').hidden = other.childElementCount > 0;
    this.updateStockStates();
  }

  isLocalStore(store) {
    return store.dataset.country.trim() === getSourceDomain();
  }

  /**
   * Availability is only rendered for local country stores and only from a successful response. A new selection
   * clears the previous result right away, so a pending or failed request never shows another size's availability.
   */
  async syncStock() {
    const variantId = this.selectedStoreVariant;
    const sku = this.variantSku(variantId);
    const codes = this.stores.filter((store) => this.isLocalStore(store)).map((store) => store.dataset.storeCode);
    // A cached answer only holds for the store set it was queried with, so a country change or a new store page drops it.
    if (this.stockCodes !== codes.join(',')) {
      this.stockCache.clear();
      this.stockCodes = codes.join(',');
    }
    this.updateStockStates(this.stockCache.get(sku) || new Map(), variantId);
    if (!this.open || !sku || !codes.length || this.stockCache.has(sku)) return;
    const stock = await this.loadStock(sku, codes);
    if (stock) this.updateStockStates(stock, variantId);
  }

  variantSku(variantId) {
    return this.productData.variants
      .find((variant) => String(variant.id) === String(variantId))?.sku?.trim() || '';
  }

  async loadStock(sku, codes) {
    const url = getOowTheme().api?.storeStockAvailability;
    if (!url) return null;
    const stock = new Map();
    try {
      for (let index = 0; index < codes.length; index += STORE_STOCK_MAX_CODES) {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ storeCodes: codes.slice(index, index + STORE_STOCK_MAX_CODES), skuCode: sku })
        });
        const payload = await response.json();
        if (!response.ok || payload?.isSuccess !== true || !Array.isArray(payload.data)) {
          throw new Error('Store stock request failed');
        }
        payload.data.forEach((entry) => stock.set(entry.storeCode, entry.hasStock ? 'in_stock' : 'out_of_stock'));
      }
    } catch {
      return null;
    }
    this.stockCache.set(sku, stock);
    return stock;
  }

  updateStockStates(stockByStore = new Map(), variantId = this.selectedStoreVariant) {
    if (String(variantId) !== this.selectedStoreVariant) return;
    this.stores.forEach((store) => {
      const value = stockByStore.get(store.dataset.storeCode);
      const state = ['in_stock', 'out_of_stock'].includes(value) ? value : '';
      const status = store.querySelector('[data-store-status]');
      status.textContent = state ? status.getAttribute(`data-${state}`) : '';
      status.hidden = !state || !this.isLocalStore(store);
      if (status.hidden) {
        delete store.dataset.stockState;
      } else {
        store.dataset.stockState = state;
      }
    });
  }

  async loadRemainingStores() {
    if (this.loadingStores || !this.nextStoresUrl) return;
    this.loadingStores = true;
    const error = this.querySelector('[data-store-error]');
    error.hidden = true;
    try {
      while (this.nextStoresUrl) {
        const url = new URL(this.nextStoresUrl, window.location.href);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.href);
        if (!response.ok) throw new Error('Store content request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const dialog = Array.from(html.querySelectorAll('oow-product-store-stock'))
          .find((element) => element.dataset.blockId === this.dataset.blockId);
        const records = dialog?.querySelector('[data-store-records]');
        if (!records || records.dataset.nextUrl === this.nextStoresUrl) throw new Error('Invalid store page');
        this.stores.push(...Array.from(records.content.children, (node) => node.cloneNode(true)));
        this.nextStoresUrl = records.dataset.nextUrl;
        this.renderStores();
        this.syncStock();
      }
    } catch {
      error.hidden = false;
    } finally {
      this.loadingStores = false;
    }
  }
}

if (!isMasterKilled() && !window.customElements.get('oow-product-store-stock')) {
  window.customElements.define('oow-product-store-stock', OowProductStoreStock);
}

const OOW_SECTION_READY_EVENT = "oow:section:ready";
const TLAB_SWITCHER_READY_EVENT = "tlSwitcherReady";
const TLAB_SWITCHER_SELECTOR = ".tl-switcher-container.bottom-left.tl-custom-position[data-switcher]";
const TLAB_HOST_SELECTOR = "[data-oow-tlab-host]";
const TLAB_CURRENCY_CHANGE_EVENT = "tlab_currency_change";
// 切换器「点遮罩关闭」用到的结构选择器：
// 模态模式下 T-Lab 会用 .tl-overlay 把面板包起来，它本身就是遮罩层；
// 非模态模式下没有 .tl-overlay，遮罩点击由我们自己判定。
const TLAB_SWITCHER_OVERLAY_SELECTOR = ".tl-overlay";
// 真正承载内容的面板，点击落在它内部不算「点遮罩」。
const TLAB_SWITCHER_PANEL_SELECTOR = ".tl-inner";
// 兜底收起时要清掉的状态类（open/opened/active/show/shown/visible/expanded/overlay），
// 允许带 tl- / tl_ 前缀，兼容不同版本 T-Lab 的命名。
const TLAB_OPEN_STATE_CLASS_PATTERN = /^(?:tl[-_])?(?:overlay|open(?:ed)?|active|shown?|visible|expanded)$/;
const OOW_SEARCH_PLACEHOLDER_FALLBACK = "What are you looking for?";
const BOOST_CURRENCY_PRICE_SELECTOR = "span.boost-sd__format-currency";
const BOOST_INSTANT_SEARCH_ANIMATION_ATTRIBUTE = "data-oow-boost-isw-animation";
const BOOST_INSTANT_SEARCH_ANIMATION_CLOSING = "closing";
const BOOST_INSTANT_SEARCH_ANIMATION_OPEN = "open";
const BOOST_INSTANT_SEARCH_ANIMATION_OPENING = "opening";
const BOOST_INSTANT_SEARCH_CLOSE_EVENT = "boost-sd-close-isw";
const BOOST_INSTANT_SEARCH_CLOSE_SELECTOR = ".boost-sd__button-text-x--close";
const BOOST_INSTANT_SEARCH_DEFAULT_PLACEHOLDER = "Search";
const BOOST_INSTANT_SEARCH_DESKTOP_ANIMATION_DURATION = 300;
const BOOST_INSTANT_SEARCH_DESKTOP_QUERY = "screen and (min-width: 992px)";
const BOOST_INSTANT_SEARCH_DESKTOP_WRAPPER_SELECTOR = ".boost-sd__search-bar-wrapper--desktop";
const BOOST_INSTANT_SEARCH_INPUT_SELECTOR = ".boost-sd__search-widget-init-input";
const BOOST_INSTANT_SEARCH_MOBILE_ANIMATION_DURATION = 200;
const BOOST_INSTANT_SEARCH_OPEN_CLASS = "boost-sd__search-opening";
const BOOST_INSTANT_SEARCH_OPEN_EVENT = "boost-sd-open-isw";
const BOOST_INSTANT_SEARCH_READY_CLASS = "boost-sd__search-widget-init-enabled";
const BOOST_INSTANT_SEARCH_RECENT_DELETE_SELECTOR = "[data-oow-boost-isw-recent-delete]";
const BOOST_INSTANT_SEARCH_RECENT_ITEM_SELECTOR = ".boost-sd__suggestion-queries-item--recent";
const BOOST_INSTANT_SEARCH_RECENT_STORAGE_KEY = "boost-sd-recent-searches";
const BOOST_INSTANT_SEARCH_RECENT_TITLE_PLACEHOLDER = "__OOW_RECENT_SEARCH_TITLE__";
const BOOST_INSTANT_SEARCH_REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const BOOST_INSTANT_SEARCH_RESULT_SELECTOR = `${BOOST_INSTANT_SEARCH_DESKTOP_WRAPPER_SELECTOR} .boost-sd__instant-search-results, .boost-sd__search-bar-wrapper--mobile .boost-sd__instant-search-results`;
const BOOST_INSTANT_SEARCH_SEARCH_BUTTON_SELECTOR = "[data-oow-boost-isw-search-submit]";
const BOOST_INSTANT_SEARCH_TRIGGER_SELECTOR = "[data-oow-boost-isw-trigger]";
const BOOST_INSTANT_SEARCH_TRIGGER_ICON_SELECTOR = `${BOOST_INSTANT_SEARCH_TRIGGER_SELECTOR} svg.header__nav-icon`;
const BOOST_INSTANT_SEARCH_MOBILE_WRAPPER_SELECTOR = ".boost-sd__search-bar-wrapper--mobile";
const HEADER_ACTION_SELECTOR = "x-header .header__account-link, x-header .header__cart-link";
const HEADER_DROPDOWN_TRIGGER_SELECTOR = "x-header .header__primary-nav :is(mega-menu-disclosure, dropdown-menu-disclosure) > details > summary, x-header .header__primary-nav [data-oow-menu-open-trigger][data-oow-menu-key]";
const BOOST_PRODUCT_ITEM_SELECTOR = ".boost-sd__product-item";
const BOOST_SELECT_OPTION_BUTTON_SELECTOR = ".boost-sd__button--select-option";
const MOBILE_PRODUCT_CARD_QUERY = "screen and (max-width: 699px)";
const PRODUCT_CARD_SWATCH_CONTROL_SELECTOR = ".product-card__swatches input[type='radio']";
const PRODUCT_CARD_SWATCH_TOOLTIP_SELECTOR = ".product-card__swatches .color-swatch[data-tooltip]";
const PRODUCT_CARD_SWATCH_TOOLTIP_VISIBLE_CLASS = "is-mobile-tooltip-visible";
const PRODUCT_CARD_SELECTOR = "product-card";
const MOBILE_PRODUCT_MEDIA_SELECTOR = "product-gallery[data-oow-mobile-media]";
const mobileProductMediaControllers = new Map();
const mobileProductMediaInitiallyPositioned = new WeakSet();
const sizeSelections = new Map();
let sizeSelectorInitialized = false;
let productStickyBarFooterThresholdInitialized = false;

const FREE_SHIPPING_BAR_FALLBACK_CURRENCY = "USD";

function getFreeShippingDisplayCurrency(fallbackCurrency) {
  const translationLab = window.TranslationLab;
  const settings = translationLab?.switcherSettings;
  const currencyService = translationLab?.currencyService;

  if (settings && currencyService?.getCurrentCurrencyCode) {
    const currentLanguage = settings.languageList?.find((language) => language.iso_code === settings.currentLocale);

    if (currentLanguage) {
      const currencyCode = currencyService.getCurrentCurrencyCode(
        settings.langCurrencies,
        currentLanguage,
        settings.shopCurrencyCode
      );

      if (currencyCode) {
        return currencyCode;
      }
    }
  }

  return window.Shopify?.currency?.active || fallbackCurrency || FREE_SHIPPING_BAR_FALLBACK_CURRENCY;
}

function convertFreeShippingAmount(amount, fromCurrency, toCurrency) {
  if (!Number.isFinite(amount) || fromCurrency === toCurrency) {
    return amount;
  }

  const currency = window.Currency;

  if (!currency || typeof currency.convert !== "function") {
    return null;
  }

  const converted = currency.convert(amount, fromCurrency, toCurrency);

  return Number.isFinite(converted) ? converted : null;
}

/**
 * Cart page / cart drawer free shipping progress bar. The threshold and the cart totals are
 * kept in the store currency; only the displayed remaining amount is converted to the
 * shopper's browsing currency through the same Shopify rate table used by TranslationLab.
 */
class OowFreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.handleCartChanged = (event) => {
      const cart = event.detail?.cart;

      if (!cart?.items) {
        return;
      }

      const priceForItems = cart.items
        .filter((item) => item.requires_shipping)
        .reduce((sum, item) => sum + item.final_line_price, 0);
      const cartDiscount = (cart.cart_level_discount_applications || [])
        .reduce((sum, discount) => sum + discount.discount_application.total_allocated_amount, 0);

      this.setAttribute("total-price", String(priceForItems - cartDiscount));
      this.render();
    };
    this.handleCurrencyChanged = (event) => this.render(event.detail?.currencyCode);
    this.handleDocumentReady = () => this.render();

    document.addEventListener("cart:change", this.handleCartChanged);
    document.addEventListener(TLAB_CURRENCY_CHANGE_EVENT, this.handleCurrencyChanged);

    // The Shopify rate table (/services/javascripts/currencies.js) is a deferred script that
    // runs after this module, so refresh once the document has finished parsing.
    if (document.readyState !== "complete") {
      document.addEventListener("DOMContentLoaded", this.handleDocumentReady, { once: true });
    }

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this.handleCartChanged);
    document.removeEventListener(TLAB_CURRENCY_CHANGE_EVENT, this.handleCurrencyChanged);
    document.removeEventListener("DOMContentLoaded", this.handleDocumentReady);
  }

  render(currencyOverride) {
    const message = this.querySelector(".oow-free-shipping-bar__message");
    const track = this.querySelector(".oow-free-shipping-bar__track");
    const threshold = parseFloat(String(this.getAttribute("threshold") ?? "").replace(/[^0-9.]/g, ""));
    const totalPrice = parseFloat(this.getAttribute("total-price"));
    const shopCurrency = this.getAttribute("shop-currency") || FREE_SHIPPING_BAR_FALLBACK_CURRENCY;

    if (!message || !track || !Number.isFinite(threshold) || threshold <= 0 || !Number.isFinite(totalPrice)) {
      this.hidden = true;
      return;
    }

    const subtotal = totalPrice / 100;
    const remaining = Math.max(threshold - subtotal, 0);
    const percent = Math.min(100, Math.max(0, Math.round((subtotal / threshold) * 100)));

    track.style.setProperty("--progress", String(percent / 100));
    track.setAttribute("aria-valuenow", String(percent));

    if (remaining <= 0) {
      message.innerHTML = this.getAttribute("reached-message") || "";
    } else {
      const displayCurrency = currencyOverride || getFreeShippingDisplayCurrency(this.getAttribute("display-currency"));
      const convertedRemaining = convertFreeShippingAmount(remaining, shopCurrency, displayCurrency);
      const amount = convertedRemaining === null ? remaining : convertedRemaining;
      const currency = convertedRemaining === null ? shopCurrency : displayCurrency;
      const formattedAmount = new Intl.NumberFormat(window.Shopify?.locale, {
        style: "currency",
        currency
      }).format(amount);

      // The formatted amount is reused as a replacement string, so escape its dollar signs.
      message.innerHTML = (this.getAttribute("unreached-message") || "")
        .replace(/\{\{.*?\}\}/, formattedAmount.replace(/\$/g, "$$$$"));
    }

    track.setAttribute("aria-label", message.textContent.trim());
    this.hidden = false;
  }
}

if (!isMasterKilled() && !window.customElements.get("oow-free-shipping-bar")) {
  window.customElements.define("oow-free-shipping-bar", OowFreeShippingBar);
}

/**
 * Cart page / cart drawer billing currency notice. The notice is hidden by default and only
 * revealed when the shopper's browsing currency (resolved by TranslationLab) differs from the
 * store billing currency, so all charges are billed in the store currency at the live rate.
 */
class OowCartCurrencyNotice extends HTMLElement {
  connectedCallback() {
    const message = this.querySelector(".oow-cart-currency-notice__message");
    this.message = message;
    this.template = message ? message.textContent.trim() : "";

    this.handleCurrencyChanged = (event) => this.render(event.detail?.currencyCode);
    this.handleDocumentReady = () => this.render();

    document.addEventListener(TLAB_CURRENCY_CHANGE_EVENT, this.handleCurrencyChanged);

    // The Shopify rate table (/services/javascripts/currencies.js) is a deferred script that
    // runs after this module, so refresh once the document has finished parsing.
    if (document.readyState !== "complete") {
      document.addEventListener("DOMContentLoaded", this.handleDocumentReady, { once: true });
    }

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(TLAB_CURRENCY_CHANGE_EVENT, this.handleCurrencyChanged);
    document.removeEventListener("DOMContentLoaded", this.handleDocumentReady);
  }

  render(currencyOverride) {
    const shopCurrency = this.getAttribute("shop-currency") || FREE_SHIPPING_BAR_FALLBACK_CURRENCY;

    if (!this.message || !this.template) {
      this.hidden = true;
      return;
    }

    const displayCurrency = currencyOverride || getFreeShippingDisplayCurrency(this.getAttribute("display-currency"));

    if (!displayCurrency || displayCurrency === shopCurrency) {
      this.hidden = true;
      return;
    }

    this.message.textContent = this.template
      .replace(/\{\{\s*display_currency\s*\}\}/g, () => displayCurrency)
      .replace(/\{\{\s*billing_currency\s*\}\}/g, () => shopCurrency);
    this.hidden = false;
  }
}

if (!isMasterKilled() && !window.customElements.get("oow-cart-currency-notice")) {
  window.customElements.define("oow-cart-currency-notice", OowCartCurrencyNotice);
}

function syncProductStickyBarFooterThreshold() {
  if (isMasterKilled()) {
    document.querySelectorAll("[data-oow-sticky-visible]").forEach((bar) => bar.removeAttribute("data-oow-sticky-visible"));
    return;
  }

  const header = document.querySelector("x-header.header");
  const footer = document.querySelector(".shopify-section--footer");
  const isDesktop = window.matchMedia("(min-width: 700px)").matches;
  if (!footer || (isDesktop && !header)) return;

  const viewportHeight = window.innerHeight;
  const headerHeight = Math.max(0, header?.getBoundingClientRect().height || 0);
  const footerRect = footer.getBoundingClientRect();
  const visibleFooterHeight = Math.max(0, Math.min(footerRect.bottom, viewportHeight) - Math.max(footerRect.top, 0));
  const footerThresholdExceeded = visibleFooterHeight > (viewportHeight - headerHeight) / 2;

  document.querySelectorAll("product-sticky-bar").forEach((stickyBar) => {
    const form = document.forms[stickyBar.getAttribute("form")];
    if (!form) return;
    const visible = isDesktop
      ? form.getBoundingClientRect().bottom < 0 && !footerThresholdExceeded
      : visibleFooterHeight === 0;
    // Keep the native observer's class intact for the kill-switch fallback.
    stickyBar.setAttribute("data-oow-sticky-visible", String(visible));
  });
}

function initializeProductStickyBarFooterThreshold() {
  if (isMasterKilled() || !document.querySelector("product-sticky-bar")) return;
  if (productStickyBarFooterThresholdInitialized) {
    syncProductStickyBarFooterThreshold();
    return;
  }

  productStickyBarFooterThresholdInitialized = true;
  const sync = throttle(syncProductStickyBarFooterThreshold);
  document.addEventListener("scroll", sync, { passive: true });
  window.addEventListener("resize", sync, { passive: true });
  const resizeObserver = new ResizeObserver(sync);
  resizeObserver.observe(document.body);
  const header = document.querySelector("x-header.header");
  if (header) resizeObserver.observe(header);
  document.addEventListener("product:rerender", () => queueMicrotask(sync), true);
  syncProductStickyBarFooterThreshold();
}

function getSizePicker(form) {
  return Array.from(document.querySelectorAll('variant-picker[context="main_product"]'))
    .find((picker) => picker.getAttribute("form-id") === form?.getAttribute("id") && picker.querySelector("[data-oow-size-selector]"));
}

function syncSizeSelector(form) {
  if (isMasterKilled()) return;
  const picker = getSizePicker(form);
  if (!picker) return;
  const field = picker.querySelector("[data-oow-size-selector]");
  const formId = form.getAttribute("id");
  const selection = sizeSelections.get(formId) || { value: null, busy: false };
  sizeSelections.set(formId, selection);
  // Mobile popovers move to document.body while open. Form ownership remains stable.
  const inputs = Array.from(form.elements).filter((input) => input.matches("input[data-size-label]"));
  const selected = inputs.find((input) => input.value === selection.value && !input.disabled);
  // Clear unavailable sizes from memory so a later color change cannot restore them.
  if (!selected) selection.value = null;
  const sold = inputs.length > 0 && inputs.every((input) => input.disabled);
  inputs.forEach((input) => { input.checked = input === selected; });
  field.querySelector("[data-oow-size-value]").textContent = selected?.dataset.sizeLabel || field.dataset.selectLabel;
  const variantId = form.querySelector('[name="id"]');
  if (variantId) {
    variantId.disabled = !selected || selection.busy;
    variantId.value = selected?.dataset.variantId || "";
  }
  const preorder = selected?.closest("[data-oow-size-state]").dataset.oowSizeState === "preorder";
  const label = sold ? field.dataset.soldLabel : !selected ? field.dataset.selectLabel
    : preorder ? field.dataset.preorderLabel : field.dataset.addLabel;
  document.querySelectorAll("buy-buttons").forEach((buttons) => {
    if (buttons.getAttribute("form") !== formId) return;
    const button = buttons.querySelector('button[type="submit"], button[data-oow-size-cta], [data-oow-size-buttons] > button');
    if (!button) return;
    button.dataset.oowSizeCta = sold ? "sold" : selected ? "selected" : "select";
    buttons.dataset.oowSizeButtons = button.dataset.oowSizeCta;
    button.textContent = label;
    button.type = selected ? "submit" : "button";
    button.disabled = sold || selection.busy;
    button.setAttribute("aria-disabled", String(button.disabled));
    buttons.querySelectorAll(".shopify-payment-button").forEach((payment) => {
      payment.hidden = !selected || selection.busy;
    });
  });
}

function openSizeSelector(form) {
  const field = getSizePicker(form)?.querySelector("[data-oow-size-selector]");
  if (!field) return;
  // The size fieldset also hosts the Size Guide entry, so the dropdown trigger is resolved through its
  // own hook instead of the first "button[aria-controls]" in document order.
  const trigger = field.querySelector("[data-oow-size-trigger]");
  const popover = document.getElementById(trigger.getAttribute("aria-controls"));
  // Scroll before the mobile dialog locks the document.
  field.scrollIntoView({ block: "center", behavior: "instant" });
  trigger.focus({ preventScroll: true });
  popover.show();
}

function initializeSizeSelector() {
  document.querySelectorAll('variant-picker[context="main_product"] [data-oow-size-selector]').forEach((field) => {
    const form = document.forms[field.closest("variant-picker").getAttribute("form-id")];
    if (form) syncSizeSelector(form);
  });
  if (sizeSelectorInitialized) return;
  sizeSelectorInitialized = true;

  document.addEventListener("click", (event) => {
    const button = event.target.closest?.('[data-oow-size-cta="select"]');
    if (!button || isMasterKilled()) return;
    event.preventDefault();
    openSizeSelector(button.form || document.forms[button.closest("buy-buttons").getAttribute("form")]);
  });

  document.addEventListener("submit", (event) => {
    if (isMasterKilled() || !getSizePicker(event.target)) return;
    const selection = sizeSelections.get(event.target.getAttribute("id"));
    if (!selection?.value || selection.busy || event.target.querySelector('[name="id"]').disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!selection?.busy) openSizeSelector(event.target);
    }
  }, true);

  document.addEventListener("change", async (event) => {
    const input = event.target;
    if (isMasterKilled() || !input.matches?.("input[data-option-position]")) return;
    const form = input.form;
    const picker = getSizePicker(form);
    if (!picker) return;
    event.stopPropagation();
    const selection = sizeSelections.get(form.getAttribute("id"));
    if (selection.busy || input.disabled) {
      syncSizeSelector(form);
      return;
    }
    const previousValue = selection.value;
    selection.value = input.hasAttribute("data-size-label") ? input.value : previousValue;
    selection.busy = true;
    const popover = input.closest("x-popover");
    const controls = Array.from(form.elements).filter((control) => control.matches("input[data-option-position]"));
    const chosen = controls.filter((control) => control.checked);
    // A complete combination lets Shopify refresh color/media even before a size is chosen.
    const sizeControl = controls.find((control) => control.hasAttribute("data-size-label"));
    if (!chosen.some((control) => control.hasAttribute("data-size-label")) && sizeControl) chosen.push(sizeControl);
    const optionValues = chosen.sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)).map((control) => control.value);
    syncSizeSelector(form);
    try {
      if (popover) await popover.hide();
      await picker.selectCombination({ optionValues, productChange: input.hasAttribute("data-product-url") });
    } catch (error) {
      selection.value = previousValue;
      console.error("[OOW extension] size selection failed", error);
    } finally {
      selection.busy = false;
      syncSizeSelector(document.forms[form.getAttribute("id")] || form);
    }
  }, true);

  document.addEventListener("product:rerender", (event) => {
    if (!getSizePicker(event.target)) return;
    const formId = event.target.getAttribute("id");
    queueMicrotask(() => {
      const form = document.forms[formId];
      if (form) syncSizeSelector(form);
    });
  }, true);
  document.addEventListener("variant:change", (event) => syncSizeSelector(event.target));
}

let patchesApplied = false;
let boostInstantSearchActiveTrigger = null;
let boostInstantSearchContentFrame = null;
let boostInstantSearchContentObserver = null;
let boostInstantSearchEmbedEnabled = window.boostWidgetIntegration?.taeSettings?.instantSearch?.enabled === true;
let boostInstantSearchHeaderObserver = null;
let boostInstantSearchInitialized = false;
let boostInstantSearchAnimationFrame = null;
let boostInstantSearchCloseDispatching = false;
let boostInstantSearchCloseTimer = null;
let boostInstantSearchOpenTimer = null;
let boostInstantSearchPendingInput = null;
let boostInstantSearchPendingQueryReset = false;
let boostInstantSearchPendingUrlQueryReset = false;
let boostInstantSearchPendingTrigger = null;
let boostInstantSearchRestoreFocusOnClose = true;
let boostInstantSearchStateObserver = null;
let boostInstantSearchTriggerState = null;
let boostThemeQuickBuyInitialized = false;
let headerDismissalInitialized = false;
let headerScrollStateInitialized = false;
let productCardSwatchTooltipInitialized = false;
let boostCardHoverMediaInitialized = false;
let productCardColorUrlInitialized = false;
let productMediaPageTopInitialized = false;
let tlabLocalizationInitialized = false;
let tlabSwitcherObserver = null;
let tlabSwitcherOrigin = null;
let tlabCurrencyPriceObserver = null;
let tlabCurrencyConversionFrame = null;
// 每个已挂载切换器一份状态：{ modal } 表示当前是否处于模态（带 .tl-overlay）模式。
const tlabSwitcherDismissals = new Map();
let productCurrencyCodeObserver = null;

function getOowTheme() {
  window.oowTheme = window.oowTheme || {};
  return window.oowTheme;
}

function getFeatures() {
  return getOowTheme().features || {};
}

function isMasterKilled() {
  return getFeatures().masterKillSwitch === true;
}

function isBoostThemeQuickBuyEnabled() {
  return !isMasterKilled() && getFeatures().product?.boostThemeQuickBuy?.enabled !== false;
}

function runSafely(moduleName, callback) {
  try {
    callback();
  } catch (error) {
    console.error(`[OOW extension] ${moduleName} failed`, error);
  }
}

function dismissHeaderSection(event) {
  const dismissButton = event.target.closest?.("[data-oow-dismiss-header-section]");

  if (!dismissButton) {
    return;
  }

  const dismissibleSection = dismissButton.closest("[data-oow-dismissible-header-section]");

  if (!dismissibleSection) {
    return;
  }

  const sectionType = dismissibleSection.dataset.oowDismissibleHeaderSection;
  const storageKey = dismissibleSection.dataset.oowDismissStorageKey;

  try {
    sessionStorage.setItem(storageKey, "true");
  } catch (error) {
    // Keep the current-page dismissal working when storage is unavailable.
  }

  document.documentElement.setAttribute(`data-oow-dismissed-${sectionType}`, "");
  (dismissibleSection.closest(".shopify-section") || dismissibleSection).hidden = true;

  if (sectionType === "announcement-bar") {
    document.documentElement.style.setProperty("--announcement-bar-height", "0px");
  }
}

function initializeHeaderDismissal() {
  if (headerDismissalInitialized) {
    return;
  }

  document.addEventListener("click", dismissHeaderSection);
  headerDismissalInitialized = true;
}

let headerScrollElement;
let headerResizeFrame;
let headerResizeVersion = 0;

function cleanupHeaderResize() {
  headerResizeVersion++;
  cancelAnimationFrame(headerResizeFrame);
  document.documentElement.removeAttribute("data-oow-header-resizing");
}

const syncHeaderScrollState = throttle(() => {
  if (!headerScrollElement?.isConnected) headerScrollElement = document.querySelector("x-header.header");
  const header = headerScrollElement;
  const scrolled = window.scrollY > 0;
  if (!header || header.hasAttribute("data-oow-scrolled") === scrolled) return;

  cleanupHeaderResize();
  const version = headerResizeVersion;
  const guardAnchoring = !isMasterKilled() && typeof header.getAnimations === "function" && window.matchMedia("(min-width: 1000px)").matches;
  if (guardAnchoring) document.documentElement.setAttribute("data-oow-header-resizing", "");
  header.toggleAttribute("data-oow-scrolled", scrolled);
  if (!guardAnchoring) return;

  headerResizeFrame = requestAnimationFrame(() => {
    const transitions = header.getAnimations().filter((animation) =>
      ["grid-template-rows", "row-gap"].includes(animation.transitionProperty)
    );
    Promise.allSettled(transitions.map((animation) => animation.finished)).then(() => {
      if (version !== headerResizeVersion) return;
      // Keep anchoring suppressed through the final layout/paint, including when
      // reduced motion makes the size change immediate. Ignore cancelled old runs.
      headerResizeFrame = requestAnimationFrame(() => {
        headerResizeFrame = requestAnimationFrame(() => {
          if (version === headerResizeVersion) cleanupHeaderResize();
        });
      });
    });
  });
});

let boostToolbarStickyCleanup;
let boostSearchTitleCleanup;

function initializeBoostSearchTitle() {
  boostSearchTitleCleanup?.();
  boostSearchTitleCleanup = null;
  if (isMasterKilled()) return;

  const title = document.querySelector("title[data-oow-search-title]");
  const scope = document.getElementById("main");
  if (!title || !scope) return;

  const resultSelector = ".boost-sd__search-result-toolbar-result[data-total-product]";
  const noResultSelector = ".boost-sd__no-search-result-page";
  const watchedSelector = `${resultSelector}, ${noResultSelector}`;
  const syncTitle = () => {
    if (isMasterKilled()) return;
    const searchPage = scope.querySelector(".boost-sd-container-search-page");
    const value = searchPage?.querySelector(resultSelector)?.getAttribute("data-total-product");
    const count = value !== null && value !== undefined && /^\d+$/.test(value)
      ? Number(value) : searchPage?.querySelector(noResultSelector) ? 0 : null;
    let nextTitle = title.dataset.oowSearchTitle;
    if (Number.isSafeInteger(count) && count >= 0) {
      const resultText = count === 0 ? title.dataset.oowSearchTitleZero
        : count === 1 ? title.dataset.oowSearchTitleOne
          : title.dataset.oowSearchTitleMany.replace("__OOW_SEARCH_COUNT__", String(count));
      const queryText = resultText.replace("__OOW_SEARCH_QUERY__", () => title.dataset.oowSearchTitleQuery);
      nextTitle = `${title.dataset.oowSearchTitlePrefix}: ${queryText}${title.dataset.oowSearchTitleSuffix}`;
    }
    // Boost replaces the first digit sequence, which may belong to the query.
    if (title.textContent !== nextTitle) title.textContent = nextTitle;
  };
  const observer = new MutationObserver((records) => {
    if (records.some((record) => title.contains(record.target)
      || (record.type === "attributes" && record.target.matches(resultSelector))
      || [...record.addedNodes, ...record.removedNodes].some((node) =>
        node.nodeType === Node.ELEMENT_NODE && (node.matches(watchedSelector) || node.querySelector(watchedSelector))
      ))) syncTitle();
  });
  observer.observe(title, { childList: true, characterData: true, subtree: true });
  observer.observe(scope, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-total-product"] });
  syncTitle();
  boostSearchTitleCleanup = () => observer.disconnect();
}

function cleanupBoostToolbarSticky() {
  boostToolbarStickyCleanup?.();
  boostToolbarStickyCleanup = null;
}

function initializeBoostToolbarSticky() {
  cleanupBoostToolbarSticky();
  if (isMasterKilled() || !["collection", "search"].includes(window.themeVariables?.settings?.pageType) || !window.ResizeObserver) return;

  const root = document.documentElement;
  const header = document.querySelector(".shopify-section--header");
  const announcement = document.querySelector(".shopify-section--announcement-bar");
  const scopes = Array.from(document.querySelectorAll(".shopify-section--apps:has(.boost-sd__filter-product-list)"));
  if (!header || !scopes.length) return;

  const selector = ".boost-sd__toolbar-container, .boost-sd-container-search-page > .boost-sd__search-result-toolbar-container";
  const properties = new Map([[header, "--oow-boost-header-height"]]);
  if (announcement) properties.set(announcement, "--oow-boost-announcement-bar-height");
  // Read all initial sizes before writing styles. Animated updates use observer
  // entries instead of forcing layout, and only toolbar descendants inherit them.
  const heights = new Map(Array.from(properties, ([element, property]) => [property, `${element.getBoundingClientRect().height.toFixed(2)}px`]));
  heights.set("--oow-boost-announcement-bar-height", heights.get("--oow-boost-announcement-bar-height") || "0px");
  let targets = new Set();
  const applyHeights = () => {
    for (const target of targets) {
      for (const [property, height] of heights) {
        if (target.style.getPropertyValue(property) !== height) target.style.setProperty(property, height);
      }
    }
  };
  const clearTarget = (target) => {
    for (const property of heights.keys()) target.style.removeProperty(property);
  };
  const syncTargets = () => {
    const next = new Set(scopes.flatMap((scope) => Array.from(scope.querySelectorAll(selector))));
    for (const target of targets) if (!next.has(target)) clearTarget(target);
    targets = next;
    applyHeights();
  };
  const resizeObserver = new ResizeObserver((entries) => {
    let changed = false;
    for (const entry of entries) {
      const size = entry.borderBoxSize;
      const height = `${(size?.[0]?.blockSize ?? size?.blockSize ?? entry.target.getBoundingClientRect().height).toFixed(2)}px`;
      const property = properties.get(entry.target);
      if (heights.get(property) !== height) {
        heights.set(property, height);
        changed = true;
      }
    }
    if (changed) applyHeights(); // Before paint; do not defer this to another frame.
  });
  const targetObserver = new MutationObserver((records) => {
    if (records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) =>
      node.nodeType === Node.ELEMENT_NODE && (targets.has(node) || node.matches(selector) || node.querySelector(selector))
    ))) syncTargets();
  });
  for (const element of properties.keys()) resizeObserver.observe(element, { box: "border-box" });
  for (const scope of scopes) targetObserver.observe(scope, { childList: true, subtree: true });
  syncTargets();
  root.setAttribute("data-oow-boost-toolbar-sticky", "");
  boostToolbarStickyCleanup = () => {
    resizeObserver.disconnect();
    targetObserver.disconnect();
    for (const target of targets) clearTarget(target);
    root.removeAttribute("data-oow-boost-toolbar-sticky");
  };
}

let productInfoStickyCleanup;

function initializeProductInfoSticky() {
  productInfoStickyCleanup?.();
  productInfoStickyCleanup = null;
  if (isMasterKilled()) return;

  const info = document.querySelector('.shopify-section--main-product .product-info');
  const header = document.querySelector('.shopify-section--header');
  if (!info || !header) return;

  const controller = new AbortController();
  const sync = () => {
    // SafeSticky caches its initial top. Only pin panels that fit, so tall
    // product information keeps the theme's bidirectional scrolling behavior.
    const fits = window.matchMedia('(min-width: 1000px)').matches &&
      info.offsetHeight + Math.max(0, header.getBoundingClientRect().bottom) + 40 <= window.innerHeight;
    info.toggleAttribute('data-oow-sticky-fits', fits);
  };
  const observer = new ResizeObserver(sync);
  observer.observe(info);
  observer.observe(header);
  window.addEventListener('resize', sync, { signal: controller.signal, passive: true });
  sync();
  productInfoStickyCleanup = () => {
    controller.abort();
    observer.disconnect();
    info.removeAttribute('data-oow-sticky-fits');
  };
}

function isIosDevice() {
  return /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function initializeProductMediaPageTop() {
  if (productMediaPageTopInitialized) return;
  productMediaPageTopInitialized = true;
  const colorChanges = new WeakMap();
  const desktop = window.matchMedia("screen and (min-width: 1000px)");

  document.addEventListener("change", (event) => {
    const control = event.target;
    if (!(control instanceof HTMLInputElement) || !control.matches("input[data-option-position]")) return;
    const form = control.form;
    if (!form) return;
    if (control.closest('variant-picker[context="main_product"]') &&
        Array.from(control.labels || []).some((label) => label.matches(".color-swatch, .thumbnail-swatch"))) {
      colorChanges.set(form, {
        position: Number(control.dataset.optionPosition),
        atPageTop: desktop.matches && window.scrollY === 0
      });
    }
  }, true);

  document.addEventListener("variant:change", (event) => {
    const form = event.target;
    const change = colorChanges.get(form);
    colorChanges.delete(form);
    const { variant, previousVariant } = event.detail || {};
    if (!change || !variant || isMasterKilled()) return;
    if (previousVariant && variant.options?.[change.position - 1] === previousVariant.options?.[change.position - 1]) return;
    const gallery = Array.from(document.querySelectorAll("product-gallery"))
      .find((element) => element.getAttribute("form") === form.getAttribute("id"));
    const carousel = gallery?.carousel;
    if (!desktop.matches && !gallery?.hasAttribute("data-oow-variant-media-only")) return;
    if (isIosDevice() && window.matchMedia("screen and (max-width: 999px)").matches) {
      // Use the existing server-side group; unfiltered galleries keep the native target.
      const hasFilteredImages = gallery?.filteredIndexes.some((index) => carousel?.allCells[index]?.dataset.mediaType === "image");
      const hasFeaturedMedia = carousel?.cells.some((cell) => cell.dataset.mediaId === String(variant.featured_media?.id));
      if (!hasFilteredImages || !hasFeaturedMedia) return;
    }
    // product:rerender has already filtered the cells before variant:change is dispatched.
    let firstImage = carousel?.cells.find((cell) => cell.dataset.mediaType === "image");
    const hasColorGroup = gallery?.hasAttribute("data-oow-variant-media-only") &&
      carousel?.allCells.some((cell) => cell.hidden && cell.dataset.mediaType === "image");
    if (desktop.matches && !hasColorGroup) {
      const getImageGroup = (src) => {
        const filename = src?.split("?")[0].split("/").pop().split(".")[0];
        return filename?.match(/^(.+)(?:_(?:\d+|pure)|-\d+)$/)?.[1];
      };
      const group = getImageGroup(variant.featured_media?.preview_image?.src);
      firstImage = (group && carousel?.cells.find((cell) => cell.dataset.mediaType === "image" &&
        getImageGroup(cell.querySelector("img")?.getAttribute("src")) === group)) ||
        carousel?.cells.find((cell) => cell.dataset.mediaId === String(variant.featured_media?.id));
    }
    if (!firstImage) return;
    const index = carousel.cells.indexOf(firstImage);
    const syncNavigation = () => {
      gallery.querySelectorAll("product-gallery-navigation, carousel-navigation").forEach((navigation) => {
        if (navigation.carousel === carousel && navigation.items[index]) navigation.onNavigationChange(index);
      });
    };

    if (desktop.matches && !hasColorGroup && !carousel.isScrollable) {
      queueMicrotask(() => {
        if (isMasterKilled() || !gallery.isConnected) return;
        firstImage.scrollIntoView({ block: "start", behavior: "smooth" });
        syncNavigation();
      });
      return;
    }

    if (!carousel.isScrollable) {
      const media = variant.featured_media;
      const nativeTarget = media && media.id !== previousVariant?.featured_media?.id
        ? gallery.querySelector(`[data-media-id="${media.id}"]`) : null;
      const target = nativeTarget || firstImage;
      const property = "scroll-margin-block-start";
      const previousValue = target.style.getPropertyValue(property);
      const previousPriority = target.style.getPropertyPriority(property);
      const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
      const firstMargin = parseFloat(getComputedStyle(firstImage).scrollMarginBlockStart) || 0;
      const destination = change.atPageTop && desktop.matches && hasColorGroup ? 0
        : window.scrollY + firstImage.getBoundingClientRect().top - padding - firstMargin;
      // Redirect the native featured-media scroll in capture phase, before it starts.
      target.style.setProperty(property, `${window.scrollY + target.getBoundingClientRect().top - padding - destination}px`, "important");
      if (!nativeTarget) target.scrollIntoView({ block: "start", behavior: "smooth" });
      queueMicrotask(() => {
        if (previousValue) target.style.setProperty(property, previousValue, previousPriority);
        else target.style.removeProperty(property);
      });
    }
    queueMicrotask(() => {
      if (isMasterKilled() || !gallery.isConnected) return;
      if (carousel.isScrollable) carousel.select(index, { instant: true });
      syncNavigation();
    });
  }, true);
}

function initializeMobileProductMedia(root = document) {
  for (const [gallery, cleanup] of mobileProductMediaControllers) {
    if (isMasterKilled() || !gallery.isConnected || root.contains(gallery)) {
      cleanup();
      mobileProductMediaControllers.delete(gallery);
    }
  }
  if (isMasterKilled()) return;

  for (const gallery of root.querySelectorAll(MOBILE_PRODUCT_MEDIA_SELECTOR)) {
    const carousel = gallery.carousel;
    if (!carousel || typeof carousel.select !== "function") continue;

    const controller = new AbortController();
    const { signal } = controller;
    const mobile = window.matchMedia("screen and (max-width: 999px)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const openOnImageTap = gallery.dataset.oowMobileMediaOpenOnTap !== "false";
    const snap = gallery.dataset.oowMobileMediaScrollMode !== "smooth";
    const iosTouch = isIosDevice() && snap;
    getFeatures().product.mobileMedia = { openOnImageTap, scrollMode: snap ? "snap" : "smooth" };
    const enabled = () => mobile.matches && !isMasterKilled();
    const canOpen = () => enabled() && openOnImageTap && gallery.hasAttribute("allow-zoom");
    const imageAttributes = new Map();
    let gesture = null;
    let touchGesture = null;
    let suppressClickUntil = 0;
    let lightbox = null;
    let openingImage = null;

    // Core measures before the mobile width/ratio is applied. Observe the cells,
    // since its own capped height may not resize again until the first swipe.
    const syncMediaHeight = () => {
      if (!enabled()) {
        carousel.style.removeProperty("max-height");
        return;
      }
      const height = carousel.selectedCell?.getBoundingClientRect().height;
      if (height) carousel.style.maxHeight = `${height}px`;
    };
    const mediaResizeObserver = new ResizeObserver(syncMediaHeight);
    for (const cell of carousel.allCells) mediaResizeObserver.observe(cell);
    carousel.addEventListener("carousel:change", syncMediaHeight, { signal });
    carousel.addEventListener("carousel:settle", syncMediaHeight, { signal });
    carousel.addEventListener("carousel:filter", syncMediaHeight, { signal });
    mobile.addEventListener("change", syncMediaHeight, { signal });

    const restoreImageAttributes = () => {
      for (const [image, attributes] of imageAttributes) {
        for (const [name, value] of Object.entries(attributes)) {
          if (value === null) image.removeAttribute(name);
          else image.setAttribute(name, value);
        }
      }
      imageAttributes.clear();
    };
    const syncAccessibility = () => {
      restoreImageAttributes();
      gesture = null;
      touchGesture = null;
      if (!canOpen()) return;
      for (const cell of carousel.cells) {
        if (cell.dataset.mediaType !== "image") continue;
        const image = cell.querySelector(":scope > img");
        if (!image) continue;
        imageAttributes.set(image, Object.fromEntries(
          ["role", "tabindex", "aria-label", "aria-haspopup"].map((name) => [name, image.getAttribute(name)])
        ));
        image.setAttribute("role", "button");
        image.setAttribute("tabindex", "0");
        image.setAttribute("aria-haspopup", "dialog");
        image.setAttribute("aria-label", [gallery.dataset.oowMobileMediaZoomLabel, image.alt].filter(Boolean).join(": "));
      }
    };
    const closeOnBackgroundTap = (event) => {
      if (!enabled()) return;
      if (event.originalEvent?.target?.matches(".pswp__item, .pswp__zoom-wrap, .pswp__container")) {
        event.preventDefault();
        lightbox.pswp?.close();
      }
    };
    const restoreOpeningImageFocus = () => {
      if (canOpen() && openingImage?.isConnected && !openingImage.parentElement.hidden) {
        openingImage.focus({ preventScroll: true });
      }
      openingImage = null;
    };
    // Configure the existing lightbox before the core lightbox:open listener runs.
    gallery.addEventListener("lightbox:open", () => {
      if (!enabled() || lightbox) return;
      lightbox = gallery.lightBox;
      lightbox.on("tapAction", closeOnBackgroundTap);
      lightbox.on("destroy", restoreOpeningImageFocus);
    }, { capture: true, signal });

    const openImage = (image) => {
      if (!canOpen() || image?.tagName !== "IMG") return;
      const cell = image.parentElement;
      const imageCells = carousel.cells.filter((item) => item.dataset.mediaType === "image");
      const index = imageCells.indexOf(cell);
      if (index < 0) return;
      openingImage = image;
      image.focus({ preventScroll: true });
      gallery.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true, detail: { index } }));
    };
    carousel.addEventListener("click", (event) => {
      if (!enabled()) return;
      if (Date.now() < suppressClickUntil && event.detail !== 0) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      openImage(event.target);
    }, { capture: true, signal });
    carousel.addEventListener("keydown", (event) => {
      if (!canOpen() || !imageAttributes.has(event.target) || !["Enter", " "].includes(event.key)) return;
      event.preventDefault();
      if (!event.repeat) openImage(event.target);
    }, { signal });

    if (iosTouch) {
      const cancelTouchGesture = () => {
        if (touchGesture) suppressClickUntil = Date.now() + 500;
        touchGesture = null;
      };
      carousel.addEventListener("touchstart", (event) => {
        if (event.touches.length !== 1) return cancelTouchGesture();
        if (!enabled() || event.target.closest("button, a, input, video-media, model-media, iframe")) return;
        const touch = event.touches[0];
        touchGesture = { id: touch.identifier, x: touch.clientX, y: touch.clientY, index: carousel.selectedIndex, axis: null };
        suppressClickUntil = 0;
      }, { signal, passive: true });
      carousel.addEventListener("touchmove", (event) => {
        if (!touchGesture) return;
        if (!enabled() || event.touches.length !== 1) return cancelTouchGesture();
        const touch = event.touches[0];
        if (touch.identifier !== touchGesture.id) return;
        const dx = Math.abs(touch.clientX - touchGesture.x);
        const dy = Math.abs(touch.clientY - touchGesture.y);
        if (Math.max(dx, dy) > 10) {
          touchGesture.axis ||= dx > dy ? "x" : "y";
          suppressClickUntil = Date.now() + 500;
        }
        if (touchGesture.axis === "x") {
          if (event.cancelable) event.preventDefault();
          else cancelTouchGesture();
        }
      }, { signal, passive: false });
      carousel.addEventListener("touchend", (event) => {
        if (!touchGesture) return;
        const touch = Array.from(event.changedTouches).find((item) => item.identifier === touchGesture.id);
        if (!touch) return;
        const { x, y, index, axis } = touchGesture;
        touchGesture = null;
        const dx = touch.clientX - x;
        const dy = touch.clientY - y;
        if (Math.max(Math.abs(dx), Math.abs(dy)) > 10) suppressClickUntil = Date.now() + 500;
        if (!enabled() || axis !== "x" || Math.abs(dx) < 30 || Math.abs(dx) <= Math.abs(dy)) return;
        const direction = (dx < 0 ? 1 : -1) * (document.dir === "rtl" ? -1 : 1);
        carousel.select(Math.max(0, Math.min(index + direction, carousel.cells.length - 1)), { instant: reducedMotion.matches });
      }, { signal });
      carousel.addEventListener("touchcancel", cancelTouchGesture, { signal });
      document.addEventListener("touchstart", (event) => {
        if (event.touches.length > 1) cancelTouchGesture();
      }, { signal, passive: true });
    }

    carousel.addEventListener("pointerdown", (event) => {
      if (iosTouch && event.pointerType === "touch") return;
      if (!enabled()) return;
      if (!event.isPrimary) {
        gesture = null;
        suppressClickUntil = Date.now() + 500;
        return;
      }
      // Leave embedded media players and their controls to the theme/browser.
      if (event.button !== 0 || event.target.closest("button, a, input, video-media, model-media, iframe")) return;
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, index: carousel.selectedIndex, axis: null };
      suppressClickUntil = 0;
    }, { signal });
    document.addEventListener("pointermove", (event) => {
      if (!gesture || gesture.id !== event.pointerId) return;
      const dx = Math.abs(event.clientX - gesture.x);
      const dy = Math.abs(event.clientY - gesture.y);
      if (Math.max(dx, dy) > 10) {
        gesture.axis ||= dx > dy ? "x" : "y";
        suppressClickUntil = Date.now() + 500;
      }
    }, { signal, passive: true });
    document.addEventListener("pointerup", (event) => {
      if (!gesture || gesture.id !== event.pointerId) return;
      const { x, y, index, axis } = gesture;
      gesture = null;
      const dx = event.clientX - x;
      const dy = event.clientY - y;
      if (Math.max(Math.abs(dx), Math.abs(dy)) > 10) suppressClickUntil = Date.now() + 500;
      if (!enabled() || !snap || axis === "y" || Math.abs(dx) < 30 || Math.abs(dx) <= Math.abs(dy)) return;
      const direction = (dx < 0 ? 1 : -1) * (document.dir === "rtl" ? -1 : 1);
      carousel.select(Math.max(0, Math.min(index + direction, carousel.cells.length - 1)), { instant: reducedMotion.matches });
    }, { signal });
    document.addEventListener("pointercancel", () => {
      if (gesture) suppressClickUntil = Date.now() + 500;
      gesture = null;
    }, { signal });
    document.addEventListener("pointerdown", (event) => {
      if (!event.isPrimary && gesture) {
        gesture = null;
        suppressClickUntil = Date.now() + 500;
      }
    }, { signal });
    carousel.addEventListener("dragstart", (event) => {
      if (enabled() && snap && event.target.tagName === "IMG") event.preventDefault();
    }, { signal });
    carousel.addEventListener("carousel:filter", syncAccessibility, { signal });
    mobile.addEventListener("change", syncAccessibility, { signal });

    const cleanup = () => {
      controller.abort();
      mediaResizeObserver.disconnect();
      carousel.style.removeProperty("max-height");
      lightbox?.off("tapAction", closeOnBackgroundTap);
      lightbox?.off("destroy", restoreOpeningImageFocus);
      lightbox?.pswp?.close();
      restoreImageAttributes();
      gallery.removeAttribute("data-oow-mobile-media-ready");
    };
    mobileProductMediaControllers.set(gallery, cleanup);
    syncAccessibility();
    gallery.setAttribute("data-oow-mobile-media-ready", "");
    syncMediaHeight();

    if (mobile.matches && !mobileProductMediaInitiallyPositioned.has(gallery)) {
      mobileProductMediaInitiallyPositioned.add(gallery);
      const hasFilteredImages = gallery.filteredIndexes.some((index) => carousel.allCells[index]?.dataset.mediaType === "image");
      const hasInitialMedia = carousel.cells.some((cell) => cell.classList.contains("is-initial"));
      if (!hasFilteredImages || !hasInitialMedia) continue;

      const initialController = new AbortController();
      let frame;
      const cancelInitialPosition = () => {
        cancelAnimationFrame(frame);
        initialResizeObserver.disconnect();
        initialController.abort();
        signal.removeEventListener("abort", cancelInitialPosition);
      };
      const positionFirstImage = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          if (!enabled() || !gallery.isConnected) {
            cancelInitialPosition();
            return;
          }
          if (!carousel.clientWidth || !carousel.clientHeight) return;
          const firstImage = carousel.cells.find((cell) => cell.dataset.mediaType === "image");
          cancelInitialPosition();
          if (!firstImage) return;
          const index = carousel.cells.indexOf(firstImage);
          carousel.select(index, { instant: true });
          gallery.querySelectorAll("product-gallery-navigation, carousel-navigation").forEach((navigation) => {
            if (navigation.carousel === carousel && navigation.items[index]) navigation.onNavigationChange(index);
          });
        });
      };
      const initialResizeObserver = new ResizeObserver(positionFirstImage);
      initialResizeObserver.observe(carousel);
      for (const type of ["pointerdown", "touchstart", "keydown", "change"]) {
        document.addEventListener(type, cancelInitialPosition, { capture: true, passive: true, signal: initialController.signal });
      }
      window.addEventListener("pagehide", cancelInitialPosition, { signal: initialController.signal });
      signal.addEventListener("abort", cancelInitialPosition, { once: true });
      positionFirstImage();
    }
  }
}

function initializeHeaderScrollState() {
  syncHeaderScrollState();

  if (headerScrollStateInitialized) {
    return;
  }

  window.addEventListener("scroll", syncHeaderScrollState, { passive: true });
  headerScrollStateInitialized = true;
}

function getTlabSwitcher() {
  if (tlabSwitcherOrigin?.switcher?.isConnected) {
    return tlabSwitcherOrigin.switcher;
  }

  return document.querySelector(TLAB_SWITCHER_SELECTOR);
}

function rememberTlabSwitcherOrigin(switcher) {
  if (tlabSwitcherOrigin?.switcher === switcher) {
    return;
  }

  tlabSwitcherOrigin = {
    nextSibling: switcher.nextSibling,
    parent: switcher.parentNode,
    switcher
  };
}

function localizeTlabSwitcher(host, switcher) {
  const preferencesText = host.dataset.oowTlabPreferences;
  const confirmAndShopText = host.dataset.oowTlabConfirmAndShop;
  const inner = switcher.querySelector(".tl-inner");
  const submitButton = switcher.querySelector(".tl-submit-btn");

  if (inner && preferencesText) {
    let preferencesHeading = inner.querySelector(":scope > [data-oow-tlab-preferences]");

    if (!preferencesHeading) {
      preferencesHeading = document.createElement("div");
      preferencesHeading.className = "oow-tlab-preferences";
      preferencesHeading.dataset.oowTlabPreferences = "";
      preferencesHeading.setAttribute("aria-level", "2");
      preferencesHeading.setAttribute("role", "heading");
      inner.prepend(preferencesHeading);
    }

    preferencesHeading.textContent = preferencesText;
  }

  if (submitButton && confirmAndShopText) {
    submitButton.textContent = confirmAndShopText;
  }
}

// 取遮罩层：切换器自身可能就是 .tl-overlay，否则优先在自己的子树里找，
// 找不到再退回全局（T-Lab 有时把 .tl-overlay 挂在 body 下而不是容器内）。
function getTlabSwitcherOverlay(switcher) {
  if (switcher.matches(TLAB_SWITCHER_OVERLAY_SELECTOR)) {
    return switcher;
  }

  return switcher.querySelector(TLAB_SWITCHER_OVERLAY_SELECTOR) || document.querySelector(TLAB_SWITCHER_OVERLAY_SELECTOR);
}

// 综合 display / visibility / pointer-events / 实际盒模型尺寸判断是否真的可见，
// 光看 class 不可靠，因为 T-Lab 可能留着 class 但已把元素隐藏。
function isTlabElementVisible(element) {
  if (!element || element.hidden) {
    return false;
  }

  const style = getComputedStyle(element);

  if (style.display === "none" || style.visibility === "hidden" || style.pointerEvents === "none") {
    return false;
  }

  const rect = element.getBoundingClientRect();

  return rect.width > 0 && rect.height > 0;
}

// In modal mode T-Lab wraps the panel in ".tl-overlay", which is the mask itself.
function isTlabSwitcherOpen(switcher) {
  const overlay = getTlabSwitcherOverlay(switcher);
  const state = tlabSwitcherDismissals.get(switcher);

  // 有遮罩层即模态模式：遮罩可见 == 面板展开，同时把 modal 标记回写进状态。
  if (overlay) {
    if (state) {
      state.modal = true;
    }

    return isTlabElementVisible(overlay);
  }

  // 非模态模式下遮罩层会消失，此时改看面板 .tl-inner 是否可见。
  // 若之前记录过 modal，说明遮罩刚被移除、正处在关闭动画中，直接判定为已关闭，
  // 避免把「正在关闭」误判成「还开着」而重复触发关闭。
  return state?.modal ? false : isTlabElementVisible(switcher.querySelector(TLAB_SWITCHER_PANEL_SELECTOR));
}

/*
  找到 T-Lab 自带的关闭按钮 —— 这是唯一不会残留多余 DOM 的收起方式。
  依次尝试：显式 data-tl-close → class / aria-label / title 里带 close 的交互元素 → 遮罩层直接子按钮。
  始终排除 .tl-submit-btn（那是「确认并购物」，点了会跳站），并要求元素当前可见。
*/
function findTlabCloseButton(scope) {
  const selectors = [
    "[data-tl-close]",
    "[class*='close' i]",
    "[aria-label*='close' i]",
    "[title*='close' i]",
    ":scope > button, :scope > [role='button']"
  ];

  for (const selector of selectors) {
    const elements = Array.from(scope.querySelectorAll(selector));
    const button = elements.find((element) => {
      return !element.matches(".tl-submit-btn") && isTlabElementVisible(element);
    });

    if (button) {
      return button;
    }
  }

  return null;
}

// 兜底收起：Toggle 掉容器 / 遮罩 / 面板 / wrapper 上所有表示「展开」的 class。
// 只在官方 API 和模拟点击都无效时才走到这里，属于最后手段。
function collapseTlabSwitcher(switcher) {
  const elements = [
    switcher,
    getTlabSwitcherOverlay(switcher),
    switcher.querySelector(TLAB_SWITCHER_PANEL_SELECTOR),
    switcher.querySelector(".tl-wrapper")
  ];

  elements.forEach((element) => {
    if (!element) {
      return;
    }

    const openClasses = Array.from(element.classList).filter((name) => TLAB_OPEN_STATE_CLASS_PATTERN.test(name));

    if (openClasses.length) {
      element.classList.remove(...openClasses);
    }
  });
}

/*
  最后的兜底：直接隐藏遮罩。仅当遮罩是独立元素时生效，
  若遮罩就是容器本身则跳过（隐藏会把触发器一起藏掉）。
*/
function hideTlabSwitcherOverlay(switcher) {
  const overlay = getTlabSwitcherOverlay(switcher);

  if (!overlay || overlay === switcher || !isTlabElementVisible(overlay)) {
    return;
  }

  overlay.dataset.oowTlabOverridden = "";
  overlay.hidden = true;
}

// 清掉上一次强制隐藏留下的印记，保证下次点击触发器还能正常展开。
function releaseTlabSwitcherOverride(switcher) {
  const overlay = getTlabSwitcherOverlay(switcher);

  if (overlay?.hasAttribute("data-oow-tlab-overridden")) {
    overlay.removeAttribute("data-oow-tlab-overridden");
    overlay.hidden = false;
  }
}

/*
  关闭切换器：优先点它自己的关闭按钮（等价于用户手动关闭，不会残留 div.bottom-left）。
  80ms 后若仍展开，再降级摘 class 强制收起。
*/
function closeTlabSwitcher(switcher) {
  const overlay = getTlabSwitcherOverlay(switcher);
  const closeButton = findTlabCloseButton(overlay || switcher);

  closeButton?.click();

  window.setTimeout(() => {
    if (!isTlabSwitcherOpen(switcher)) {
      return;
    }

    collapseTlabSwitcher(switcher);
    window.setTimeout(() => hideTlabSwitcherOverlay(switcher), 350);
  }, 80);
}

// 给切换器补上「点击遮罩关闭」的能力（T-Lab 原生不带）。
// 同一个切换器只初始化一次，由 tlabSwitcherDismissals 去重。
function initializeTlabSwitcherDismiss(switcher) {
  if (tlabSwitcherDismissals.has(switcher)) {
    return;
  }

  tlabSwitcherDismissals.set(switcher, { modal: false });

  // The state is read before T-Lab reacts to the click, so opening clicks are never undone.
  // 必须在 pointerdown（捕获阶段）先快照展开状态：
  // 等到 click 时 T-Lab 可能已经把面板打开了，那样「展开的那一次点击」会被误判成遮罩点击而马上关掉。
  let openedBeforeClick = false;

  document.addEventListener("pointerdown", () => {
    releaseTlabSwitcherOverride(switcher);
    openedBeforeClick = isTlabSwitcherOpen(switcher);
  }, true);

  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;

    if (!target) {
      return;
    }

    // 两个条件缺一不可：点击前必须是展开的，且本次点击没有落在面板内部（即点到了遮罩）。
    if (!openedBeforeClick || target.closest(TLAB_SWITCHER_PANEL_SELECTOR)) {
      return;
    }

    // T-Lab may close the modal by itself, so only force the close when it is still open.
    window.requestAnimationFrame(() => {
      if (isTlabSwitcherOpen(switcher)) {
        closeTlabSwitcher(switcher);
      }
    });
  }, true);
}

function convertTlabCurrencyPrices() {
  tlabCurrencyConversionFrame = null;

  const translationLab = window.TranslationLab;
  const settings = translationLab?.switcherSettings;
  const currencyService = translationLab?.currencyService;
  const currencyConverter = translationLab?.Currency;

  document.querySelectorAll(BOOST_CURRENCY_PRICE_SELECTOR).forEach((price) => {
    price.classList.add("tlab-currency-format");
  });

  if (!settings || !currencyService?.getCurrentCurrencyCode || !currencyConverter?.convertAll) {
    return;
  }

  const currentLanguage = settings.languageList?.find((language) => language.iso_code === settings.currentLocale);

  if (!currentLanguage) {
    return;
  }

  const currencyCode = currencyService.getCurrentCurrencyCode(
    settings.langCurrencies,
    currentLanguage,
    settings.shopCurrencyCode
  );

  currencyConverter.convertAll(
    settings.shopCurrencyCode,
    currencyCode,
    currencyCode,
    settings.currencyFormatClass
  );
}

function scheduleTlabCurrencyConversion() {
  if (tlabCurrencyConversionFrame !== null) {
    return;
  }

  tlabCurrencyConversionFrame = window.requestAnimationFrame(convertTlabCurrencyPrices);
}

function initializeProductCurrencyCodes() {
  if (productCurrencyCodeObserver) {
    return;
  }

  const selector = [
    "price-list sale-price > .tlab-currency-format",
    "price-list compare-at-price > .tlab-currency-format",
    "price-list .product-price__discount-badge .tlab-currency-format",
    "product-card on-sale-badge .tlab-currency-format",
    ".boost-sd__product-label--sale .boost-sd__format-currency",
    ".boost-sd__suggestion-queries-item-badge--sale .boost-sd__format-currency",
    ".cart-page .order-summary__body > tr > td.text-end .tlab-currency-format",
    ".cart-page .cart-recap__total > .text-lg .tlab-currency-format",
    ".cart-page .cart-recap__button-price .tlab-currency-format",
    ".cart-drawer .cart-drawer__button-price .tlab-currency-format"
  ].join(", ");
  const pending = new Set();
  let scheduled = false;

  function synchronize(price) {
    const enabled = window.themeVariables?.settings?.currencyCodeEnabled;

    if (typeof enabled !== "boolean" || isMasterKilled() || !price.isConnected) {
      return;
    }

    const walker = document.createTreeWalker(price, NodeFilter.SHOW_TEXT);
    const texts = [];
    let node;

    while ((node = walker.nextNode())) {
      texts.push(node);
    }

    texts.forEach((text) => {
      const metadata = text.parentElement.closest("[data-currency], [data-base-currency]");
      const currency = metadata?.getAttribute("data-currency")
        || metadata?.getAttribute("data-base-currency")
        || window.Shopify?.currency?.active;

      if (!/^[A-Z]{3}$/.test(currency || "")) {
        return;
      }

      const code = new RegExp(`(^|[^A-Za-z])${currency}(?=$|[^A-Za-z])`, "g");
      const original = text.nodeValue;
      let value = original;

      if (!enabled) {
        value = value.replace(code, (_, prefix) => /^\s$/.test(prefix) ? "" : prefix);
      } else if (/\d/.test(value) && !code.test(price.textContent)) {
        value = value.replace(/(\S)(\s*)$/, `$1 ${currency}$2`);
      }

      if (value !== original) {
        text.nodeValue = value;
      }
    });
  }

  function enqueue(node) {
    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;

    if (!element) {
      return;
    }

    const price = element.closest(selector);

    if (price) {
      pending.add(price);
    }

    element.querySelectorAll(selector).forEach((item) => pending.add(item));

    if (pending.size && !scheduled) {
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        pending.forEach(synchronize);
        pending.clear();
      });
    }
  }

  // T-Lab writes text and currency metadata asynchronously after Liquid renders.
  productCurrencyCodeObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type !== "childList") {
        enqueue(mutation.target);
      } else {
        const target = mutation.target.nodeType === Node.ELEMENT_NODE ? mutation.target : mutation.target.parentElement;
        if (target?.closest(selector)) enqueue(target);
        mutation.addedNodes.forEach(enqueue);
      }
    });
  });
  productCurrencyCodeObserver.observe(document.body, {
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ["data-currency", "data-base-currency"],
    subtree: true
  });
  document.addEventListener(TLAB_CURRENCY_CHANGE_EVENT, () => enqueue(document.body));
  enqueue(document.body);
}

function mutationAddsBoostCurrencyPrice(mutation) {
  return Array.from(mutation.addedNodes).some((node) => {
    return node instanceof Element
      && (node.matches(BOOST_CURRENCY_PRICE_SELECTOR) || node.querySelector(BOOST_CURRENCY_PRICE_SELECTOR));
  });
}

function initializeTlabCurrencyPrices() {
  scheduleTlabCurrencyConversion();

  if (tlabCurrencyPriceObserver) {
    return;
  }

  document.addEventListener(TLAB_CURRENCY_CHANGE_EVENT, () => {
    scheduleTlabCurrencyConversion();
    scheduleBoostInstantSearchContentEnhancement();
  });
  tlabCurrencyPriceObserver = new MutationObserver((mutations) => {
    if (mutations.some(mutationAddsBoostCurrencyPrice)) {
      scheduleTlabCurrencyConversion();
    }
  });
  tlabCurrencyPriceObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
}

function mountTlabSwitcher(root = document) {
  const host = root.querySelector?.(TLAB_HOST_SELECTOR) || document.querySelector(TLAB_HOST_SELECTOR);
  const switcher = getTlabSwitcher();

  if (!host || !switcher) {
    return;
  }

  rememberTlabSwitcherOrigin(switcher);

  if (switcher.parentNode !== host) {
    host.append(switcher);
  }

  localizeTlabSwitcher(host, switcher);
  // 挂载完成后立刻补上遮罩关闭行为。
  initializeTlabSwitcherDismiss(switcher);
  switcher.setAttribute("data-oow-tlab-mounted", "");
  host.querySelector("[data-oow-tlab-fallback]")?.setAttribute("hidden", "");
}

function restoreTlabSwitcher() {
  const switcher = getTlabSwitcher();

  if (!switcher || !tlabSwitcherOrigin) {
    return;
  }

  const { nextSibling, parent } = tlabSwitcherOrigin;
  const targetParent = parent?.isConnected ? parent : document.body;

  if (nextSibling?.parentNode === targetParent) {
    targetParent.insertBefore(switcher, nextSibling);
  } else {
    targetParent.append(switcher);
  }

  switcher.removeAttribute("data-oow-tlab-mounted");
  document.querySelectorAll(`${TLAB_HOST_SELECTOR} [data-oow-tlab-fallback]`).forEach((fallback) => {
    fallback.removeAttribute("hidden");
  });
}

function handleTlabSwitcherReady(event) {
  const switcher = event.detail?.switcherElement;

  window.requestAnimationFrame(() => {
    if (!switcher || switcher.matches(TLAB_SWITCHER_SELECTOR)) {
      mountTlabSwitcher(document);
    }

    scheduleBoostInstantSearchContentEnhancement();
  });
}

function handleFooterSectionUnload(event) {
  const switcher = getTlabSwitcher();

  if (switcher && event.target?.contains(switcher)) {
    restoreTlabSwitcher();
  }
}

function initializeTlabLocalization(root = document) {
  mountTlabSwitcher(root);
  initializeTlabCurrencyPrices();

  if (tlabLocalizationInitialized) {
    return;
  }

  document.addEventListener(TLAB_SWITCHER_READY_EVENT, handleTlabSwitcherReady);
  document.addEventListener("shopify:section:unload", handleFooterSectionUnload);

  tlabSwitcherObserver = new MutationObserver((mutations) => {
    const switcherBecameReady = mutations.some((mutation) => mutation.target.matches?.(TLAB_SWITCHER_SELECTOR));

    if (switcherBecameReady) {
      mountTlabSwitcher(document);
    }
  });
  tlabSwitcherObserver.observe(document.documentElement, {
    attributeFilter: ["data-switcher"],
    attributes: true,
    subtree: true
  });
  tlabLocalizationInitialized = true;
}

function getBoostProductHandle(productItem) {
  try {
    const productData = JSON.parse(productItem.dataset.product || "{}");

    if (productData.handle) {
      return productData.handle;
    }
  } catch (error) {
    // Fall back to the product URL when Boost changes or omits its data payload.
  }

  const productLink = productItem.querySelector('a[href*="/products/"]');

  if (!productLink) {
    return "";
  }

  try {
    return new URL(productLink.href, window.location.origin).pathname.match(/\/products\/([^/]+)/)?.[1] || "";
  } catch (error) {
    return "";
  }
}

function getBoostQuickBuyModal(productItem, productHandle, trigger) {
  const productKey = productItem.dataset.productId || productHandle;
  const modalId = `oow-boost-quick-buy-${productKey}`;
  let modal = document.getElementById(modalId);

  if (!modal) {
    modal = document.createElement("quick-buy-modal");
    modal.id = modalId;
    modal.className = "quick-buy-modal modal";
    modal.setAttribute("handle", productHandle);
    modal.setAttribute("aria-label", trigger.getAttribute("aria-label") || "Quick buy");
    document.body.append(modal);
  }

  trigger.setAttribute("aria-controls", modalId);
  return modal;
}

function replayBoostSelectOption(trigger) {
  trigger.dataset.oowBoostQuickBuyBypass = "true";
  trigger.click();
  delete trigger.dataset.oowBoostQuickBuyBypass;
}

async function openBoostThemeQuickBuy(trigger, productItem, productHandle) {
  trigger.setAttribute("aria-busy", "true");

  try {
    const modal = getBoostQuickBuyModal(productItem, productHandle, trigger);
    await modal.show();
  } catch (error) {
    console.error("[OOW extension] Boost theme quick buy failed", error);
    trigger.removeAttribute("aria-controls");
    replayBoostSelectOption(trigger);
  } finally {
    trigger.removeAttribute("aria-busy");
  }
}

function handleBoostThemeQuickBuy(event) {
  const trigger = event.target.closest?.(BOOST_SELECT_OPTION_BUTTON_SELECTOR);

  if (!trigger || trigger.dataset.oowBoostQuickBuyBypass === "true" || !isBoostThemeQuickBuyEnabled()) {
    return;
  }

  const productItem = trigger.closest(BOOST_PRODUCT_ITEM_SELECTOR);
  const productHandle = productItem && getBoostProductHandle(productItem);

  if (!productHandle || !window.customElements.get("quick-buy-modal") || trigger.getAttribute("aria-busy") === "true") {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  void openBoostThemeQuickBuy(trigger, productItem, productHandle);
}

function initializeBoostThemeQuickBuy() {
  if (boostThemeQuickBuyInitialized) {
    return;
  }

  window.addEventListener("click", handleBoostThemeQuickBuy, true);
  boostThemeQuickBuyInitialized = true;
}

function isBoostInstantSearchReady() {
  boostInstantSearchEmbedEnabled ||= window.boostWidgetIntegration?.taeSettings?.instantSearch?.enabled === true;

  return boostInstantSearchEmbedEnabled && document.body.classList.contains(BOOST_INSTANT_SEARCH_READY_CLASS);
}

function getOowSearchPlaceholder() {
  const placeholder = getOowTheme().search?.placeholder;

  return typeof placeholder === "string" && placeholder.trim()
    ? placeholder.trim()
    : OOW_SEARCH_PLACEHOLDER_FALLBACK;
}

function syncBoostInstantSearchPlaceholders(root = document) {
  const placeholder = getOowSearchPlaceholder();
  const inputs = root.matches?.(BOOST_INSTANT_SEARCH_INPUT_SELECTOR)
    ? [root]
    : Array.from(root.querySelectorAll?.(BOOST_INSTANT_SEARCH_INPUT_SELECTOR) || []);

  inputs.forEach((input) => {
    const currentPlaceholder = input.getAttribute("placeholder")?.trim() || "";
    const previousOowPlaceholder = input.dataset.oowSearchPlaceholder;
    const canReplaceCurrentPlaceholder = !currentPlaceholder
      || currentPlaceholder === BOOST_INSTANT_SEARCH_DEFAULT_PLACEHOLDER
      || currentPlaceholder === previousOowPlaceholder;

    if (!canReplaceCurrentPlaceholder) {
      return;
    }

    if (currentPlaceholder !== placeholder) {
      input.setAttribute("placeholder", placeholder);
    }

    input.dataset.oowSearchPlaceholder = placeholder;
  });
}

function isBoostInstantSearchOpen() {
  return document.body.classList.contains(BOOST_INSTANT_SEARCH_OPEN_CLASS);
}

function getBoostInstantSearchAnimationDuration() {
  if (window.matchMedia(BOOST_INSTANT_SEARCH_REDUCED_MOTION_QUERY).matches) {
    return 0;
  }

  return window.matchMedia(BOOST_INSTANT_SEARCH_DESKTOP_QUERY).matches
    ? BOOST_INSTANT_SEARCH_DESKTOP_ANIMATION_DURATION
    : BOOST_INSTANT_SEARCH_MOBILE_ANIMATION_DURATION;
}

function getBoostInstantSearchWrapperSelector() {
  return window.matchMedia(BOOST_INSTANT_SEARCH_DESKTOP_QUERY).matches
    ? BOOST_INSTANT_SEARCH_DESKTOP_WRAPPER_SELECTOR
    : BOOST_INSTANT_SEARCH_MOBILE_WRAPPER_SELECTOR;
}

function getBoostInstantSearchAnimationState() {
  return document.documentElement.getAttribute(BOOST_INSTANT_SEARCH_ANIMATION_ATTRIBUTE);
}

function setBoostInstantSearchAnimationState(state) {
  if (state) {
    document.documentElement.setAttribute(BOOST_INSTANT_SEARCH_ANIMATION_ATTRIBUTE, state);
  } else {
    document.documentElement.removeAttribute(BOOST_INSTANT_SEARCH_ANIMATION_ATTRIBUTE);
  }
}

function isBoostInstantSearchClosing() {
  return getBoostInstantSearchAnimationState() === BOOST_INSTANT_SEARCH_ANIMATION_CLOSING;
}

function clearBoostInstantSearchAnimationFrame() {
  if (boostInstantSearchAnimationFrame !== null) {
    window.cancelAnimationFrame(boostInstantSearchAnimationFrame);
    boostInstantSearchAnimationFrame = null;
  }
}

function clearBoostInstantSearchCloseTimer() {
  window.clearTimeout(boostInstantSearchCloseTimer);
  boostInstantSearchCloseTimer = null;
}

function scheduleBoostInstantSearchOpenAnimation() {
  clearBoostInstantSearchAnimationFrame();

  const promoteToOpen = () => {
    boostInstantSearchAnimationFrame = null;

    if (!boostInstantSearchActiveTrigger
      || !isBoostInstantSearchOpen()
      || getBoostInstantSearchAnimationState() !== BOOST_INSTANT_SEARCH_ANIMATION_OPENING) {
      return;
    }

    if (!document.querySelector(getBoostInstantSearchWrapperSelector())) {
      boostInstantSearchAnimationFrame = window.requestAnimationFrame(promoteToOpen);
      return;
    }

    resetBoostInstantSearchQueryAfterOpen();

    if (getBoostInstantSearchAnimationDuration() === 0) {
      setBoostInstantSearchAnimationState(BOOST_INSTANT_SEARCH_ANIMATION_OPEN);
      return;
    }

    boostInstantSearchAnimationFrame = window.requestAnimationFrame(() => {
      boostInstantSearchAnimationFrame = null;

      if (boostInstantSearchActiveTrigger
        && isBoostInstantSearchOpen()
        && getBoostInstantSearchAnimationState() === BOOST_INSTANT_SEARCH_ANIMATION_OPENING) {
        setBoostInstantSearchAnimationState(BOOST_INSTANT_SEARCH_ANIMATION_OPEN);
      }
    });
  };

  boostInstantSearchAnimationFrame = window.requestAnimationFrame(promoteToOpen);
}

function finishBoostInstantSearchClose({ dispatchCloseEvent = false } = {}) {
  clearBoostInstantSearchCloseTimer();

  if (!boostInstantSearchActiveTrigger || !isBoostInstantSearchClosing()) {
    return;
  }

  if (dispatchCloseEvent) {
    boostInstantSearchCloseDispatching = true;
    try {
      window.dispatchEvent(new CustomEvent(BOOST_INSTANT_SEARCH_CLOSE_EVENT));
    } finally {
      boostInstantSearchCloseDispatching = false;
    }
  }

  if (boostInstantSearchActiveTrigger) {
    restoreBoostInstantSearchAfterClose();
  }
}

function scheduleBoostInstantSearchClose({ dispatchCloseEvent = false } = {}) {
  clearBoostInstantSearchCloseTimer();

  const finishClose = () => finishBoostInstantSearchClose({ dispatchCloseEvent });

  if (getBoostInstantSearchAnimationDuration() === 0) {
    finishClose();
    return;
  }

  boostInstantSearchCloseTimer = window.setTimeout(finishClose, getBoostInstantSearchAnimationDuration());
}

function cancelBoostInstantSearchClose() {
  if (!isBoostInstantSearchClosing()) {
    return false;
  }

  if (!boostInstantSearchActiveTrigger || !isBoostInstantSearchOpen()) {
    return false;
  }

  clearBoostInstantSearchCloseTimer();
  setBoostInstantSearchAnimationState(BOOST_INSTANT_SEARCH_ANIMATION_OPEN);
  return true;
}

function handleBoostInstantSearchMotionChange() {
  if (!boostInstantSearchActiveTrigger) {
    return;
  }

  if (isBoostInstantSearchClosing()) {
    finishBoostInstantSearchClose({ dispatchCloseEvent: isBoostInstantSearchOpen() });
    return;
  }

  if (isBoostInstantSearchOpen()) {
    clearBoostInstantSearchAnimationFrame();
    resetBoostInstantSearchQueryAfterOpen();
    setBoostInstantSearchAnimationState(BOOST_INSTANT_SEARCH_ANIMATION_OPEN);
    syncBoostInstantSearchPosition();
  }
}

function getBoostInstantSearchInput(trigger) {
  return document.querySelector(`x-header header-search ${BOOST_INSTANT_SEARCH_INPUT_SELECTOR}`)
    || document.querySelector(`${BOOST_INSTANT_SEARCH_DESKTOP_WRAPPER_SELECTOR} ${BOOST_INSTANT_SEARCH_INPUT_SELECTOR}`)
    || document.querySelector(`${BOOST_INSTANT_SEARCH_MOBILE_WRAPPER_SELECTOR} ${BOOST_INSTANT_SEARCH_INPUT_SELECTOR}`)
    || trigger;
}

function getBoostInstantSearchQueryInputs() {
  return document.querySelectorAll([
    `x-header header-search ${BOOST_INSTANT_SEARCH_INPUT_SELECTOR}`,
    `${BOOST_INSTANT_SEARCH_DESKTOP_WRAPPER_SELECTOR} ${BOOST_INSTANT_SEARCH_INPUT_SELECTOR}`,
    `${BOOST_INSTANT_SEARCH_MOBILE_WRAPPER_SELECTOR} ${BOOST_INSTANT_SEARCH_INPUT_SELECTOR}`
  ].join(", "));
}

function handleBoostInstantSearchQueryInput(event) {
  if ((boostInstantSearchPendingUrlQueryReset || boostInstantSearchPendingInput) && event.target instanceof HTMLInputElement
    && (event.target.matches("x-header header-search input[name='q']")
      || Array.from(getBoostInstantSearchQueryInputs()).includes(event.target))) {
    boostInstantSearchPendingUrlQueryReset = false;
    boostInstantSearchPendingInput = isBoostInstantSearchReady()
      ? null
      : { input: event.target, value: event.target.value };
  }
}

function replayBoostInstantSearchPendingInput() {
  if (!boostInstantSearchPendingInput || !isBoostInstantSearchReady()) {
    return;
  }

  const { input, value } = boostInstantSearchPendingInput;

  boostInstantSearchPendingInput = null;

  if (!input.isConnected || (!input.closest("header-search[open]") && !isBoostInstantSearchOpen())) {
    boostInstantSearchPendingUrlQueryReset = true;
    return;
  }

  // Initial Boost binding can overwrite text typed in the native fallback.
  getBoostInstantSearchQueryInputs().forEach((queryInput) => { queryInput.value = value; });
  dispatchBoostInstantSearchQueryInput(input);
}

function prepareBoostInstantSearchInitialQuery() {
  if (!boostInstantSearchPendingUrlQueryReset || !isBoostInstantSearchReady()) {
    return;
  }

  boostInstantSearchPendingUrlQueryReset = false;
  getBoostInstantSearchQueryInputs().forEach((input) => { input.value = ""; });
  boostInstantSearchPendingQueryReset = true;
}

function resetBoostInstantSearchQueryAfterOpen() {
  if (!boostInstantSearchPendingQueryReset
    || !boostInstantSearchActiveTrigger
    || !isBoostInstantSearchOpen()
    || isBoostInstantSearchClosing()) {
    return;
  }

  const input = document.querySelector(`${getBoostInstantSearchWrapperSelector()} ${BOOST_INSTANT_SEARCH_INPUT_SELECTOR}`);

  if (!input) {
    return;
  }

  // Consume before dispatch: Boost's empty input handler can emit another open event.
  boostInstantSearchPendingQueryReset = false;

  if (Array.from(getBoostInstantSearchQueryInputs()).some((queryInput) => queryInput.value !== "")) {
    return;
  }

  dispatchBoostInstantSearchQueryInput(input);
}

function dispatchBoostInstantSearchQueryInput(input) {
  // Boost synchronizes all bound inputs, including the full search-page form.
  const queryInputs = new Set(getBoostInstantSearchQueryInputs());
  const otherInputs = Array.from(document.querySelectorAll(BOOST_INSTANT_SEARCH_INPUT_SELECTOR))
    .filter((otherInput) => !queryInputs.has(otherInput))
    .map((otherInput) => [otherInput, otherInput.value]);

  try {
    input.dispatchEvent(new Event("input", { bubbles: true }));
  } finally {
    otherInputs.forEach(([otherInput, value]) => { otherInput.value = value; });
  }
}

function handleBoostInstantSearchRendered(event) {
  if (event.detail?.data?.query
    && boostInstantSearchActiveTrigger
    && isBoostInstantSearchOpen()
    && !isBoostInstantSearchClosing()
    && Array.from(getBoostInstantSearchQueryInputs()).every((input) => input.value === "")) {
    // Boost can skip a default render while an older query is still rendering.
    boostInstantSearchPendingQueryReset = true;
    scheduleBoostInstantSearchContentEnhancement();
  }
}

function focusBoostInstantSearchInput() {
  const wrapperSelector = window.matchMedia(BOOST_INSTANT_SEARCH_DESKTOP_QUERY).matches
    ? BOOST_INSTANT_SEARCH_DESKTOP_WRAPPER_SELECTOR
    : ".boost-sd__search-bar-wrapper--mobile";
  const input = document.querySelector(`${wrapperSelector} ${BOOST_INSTANT_SEARCH_INPUT_SELECTOR}`);

  if (boostInstantSearchActiveTrigger && isBoostInstantSearchOpen() && input) {
    document.querySelector("x-header.header")?.removeAttribute("data-oow-force-transparent");
    input.focus({ preventScroll: true });
  }
}

function syncBoostInstantSearchPosition() {
  if (!boostInstantSearchActiveTrigger) {
    return;
  }

  const header = document.querySelector("x-header.header");

  if (!header) {
    return;
  }

  const headerBottom = Math.min(window.innerHeight, Math.max(0, header.getBoundingClientRect().bottom));
  document.documentElement.style.setProperty("--oow-boost-isw-top", `${headerBottom}px`);
}

function getBoostInstantSearchSuggestionPanel(root, type) {
  const panel = root.querySelector(`.boost-sd__suggestion-queries-header--${type}`)
    ?.closest(".boost-sd__suggestion-queries-group");

  return panel?.querySelector(".boost-sd__suggestion-queries-item") ? panel : null;
}

function getBoostInstantSearchState(results) {
  const wrapper = results.closest(".boost-sd__search-bar-wrapper--desktop, .boost-sd__search-bar-wrapper--mobile");
  const input = wrapper?.querySelector(BOOST_INSTANT_SEARCH_INPUT_SELECTOR);

  if (!input?.value.trim()) {
    return "empty";
  }

  if (results.querySelector(".boost-sd__no-search-result")) {
    return "no-results";
  }

  const hasResults = results.querySelector(".boost-sd__suggestion-queries-item--product")
    || getBoostInstantSearchSuggestionPanel(results, "collections")
    || getBoostInstantSearchSuggestionPanel(results, "pages");

  return hasResults ? "results" : "loading";
}

function activateBoostInstantSearchTab(tab) {
  const tablist = tab.closest("[data-oow-boost-isw-tablist]");

  if (!tablist) {
    return;
  }

  const results = tablist.closest("[data-oow-boost-isw-state]");

  tablist.querySelectorAll("[data-oow-boost-isw-tab]").forEach((candidate) => {
    const isActive = candidate === tab;
    const panel = results?.querySelector(`#${CSS.escape(candidate.getAttribute("aria-controls"))}`);

    candidate.setAttribute("aria-selected", String(isActive));
    candidate.tabIndex = isActive ? 0 : -1;

    if (panel) {
      panel.hidden = !isActive;
    }
  });
}

function createBoostInstantSearchTab(type, label, panel, suffix, isActive) {
  const panelId = `oow-boost-isw-panel-${suffix}-${type}`;
  const tabId = `oow-boost-isw-tab-${suffix}-${type}`;
  const tab = document.createElement("button");

  tab.className = "oow-boost-isw__tab";
  tab.dataset.oowBoostIswTab = type;
  tab.id = tabId;
  tab.setAttribute("aria-controls", panelId);
  tab.setAttribute("aria-selected", String(isActive));
  tab.setAttribute("role", "tab");
  tab.tabIndex = isActive ? 0 : -1;
  tab.type = "button";
  tab.textContent = label;

  panel.classList.add("oow-boost-isw__tab-panel", `oow-boost-isw__tab-panel--${type}`);
  panel.dataset.oowBoostIswTabPanel = type;
  panel.hidden = !isActive;
  panel.id = panelId;
  panel.setAttribute("aria-labelledby", tabId);
  panel.setAttribute("role", "tabpanel");

  return tab;
}

function enhanceBoostInstantSearchSearchBar(form) {
  if (form.querySelector(BOOST_INSTANT_SEARCH_SEARCH_BUTTON_SELECTOR)) {
    return;
  }

  const nativeSubmit = form.querySelector(".boost-sd__search-bar-submit");
  const label = nativeSubmit?.getAttribute("aria-label")
    || form.querySelector(".boost-sd__search-bar-label")?.textContent.trim()
    || form.querySelector(BOOST_INSTANT_SEARCH_INPUT_SELECTOR)?.getAttribute("placeholder");
  const headerSearchIcon = document.querySelector(BOOST_INSTANT_SEARCH_TRIGGER_ICON_SELECTOR);

  if (!label || !headerSearchIcon) {
    return;
  }

  const searchButton = document.createElement("button");
  const searchIcon = headerSearchIcon.cloneNode(true);

  searchIcon.classList.remove("header__nav-icon");
  searchIcon.classList.add("oow-boost-isw__search-icon");
  searchButton.className = "oow-boost-isw__search-submit";
  searchButton.dataset.oowBoostIswSearchSubmit = "";
  searchButton.setAttribute("aria-label", label);
  searchButton.type = "button";
  searchButton.append(searchIcon);
  form.append(searchButton);
}

function formatBoostInstantSearchRecentString(title, template) {
  return template.replace(BOOST_INSTANT_SEARCH_RECENT_TITLE_PLACEHOLDER, title);
}

function getBoostInstantSearchRecentRecord(source) {
  const item = source.closest(BOOST_INSTANT_SEARCH_RECENT_ITEM_SELECTOR);
  const title = source.dataset.oowBoostIswRecentTitle || item?.dataset.title || "";

  return {
    extraParam: source.dataset.oowBoostIswRecentExtraParam || "",
    title: title.trim()
  };
}

function enhanceBoostInstantSearchRecentSearches(results) {
  results.querySelectorAll(BOOST_INSTANT_SEARCH_RECENT_DELETE_SELECTOR).forEach((button) => {
    const { title } = getBoostInstantSearchRecentRecord(button);

    if (!title) {
      return;
    }

    button.setAttribute("aria-label", formatBoostInstantSearchRecentString(
      title,
      `Remove recent search "${BOOST_INSTANT_SEARCH_RECENT_TITLE_PLACEHOLDER}"`
    ));
    button.hidden = false;
  });
}

function normalizeBoostInstantSearchRecentExtraParam(value) {
  return value === undefined || value === null ? "" : String(value);
}

function boostInstantSearchRecentRecordsMatch(left, right) {
  return typeof left?.title === "string"
    && left.title.trim().toLocaleLowerCase() === right.title.toLocaleLowerCase()
    && normalizeBoostInstantSearchRecentExtraParam(left.extraParam) === right.extraParam;
}

function removeBoostInstantSearchRecentStorageRecord(record) {
  const serialized = window.localStorage.getItem(BOOST_INSTANT_SEARCH_RECENT_STORAGE_KEY);

  if (serialized === null) {
    return;
  }

  const recentSearches = JSON.parse(serialized);

  if (!Array.isArray(recentSearches)) {
    throw new TypeError("Boost recent search storage is not an array");
  }

  window.localStorage.setItem(
    BOOST_INSTANT_SEARCH_RECENT_STORAGE_KEY,
    JSON.stringify(recentSearches.filter((recentSearch) => {
      return !boostInstantSearchRecentRecordsMatch(recentSearch, record);
    }))
  );
}

function announceBoostInstantSearchRecentResult(targetId, message) {
  const target = document.getElementById(targetId);

  if (!target) {
    return;
  }

  target.textContent = "";
  window.requestAnimationFrame(() => {
    target.textContent = message;
  });
}

function getBoostInstantSearchRecentFocusTarget(button) {
  const list = button.closest(".boost-sd__suggestion-queries-list");
  const buttons = Array.from(list?.querySelectorAll(BOOST_INSTANT_SEARCH_RECENT_DELETE_SELECTOR) || []);
  const index = buttons.indexOf(button);
  const results = button.closest("[data-oow-boost-isw-state]");
  const nextButton = buttons[index + 1] || buttons[index - 1];

  return nextButton
    || results?.querySelector(".boost-sd__suggestion-queries-item--popular .boost-sd__suggestion-queries-link")
    || results?.closest(".boost-sd__search-bar-wrapper--desktop, .boost-sd__search-bar-wrapper--mobile")
      ?.querySelector(BOOST_INSTANT_SEARCH_INPUT_SELECTOR);
}

function removeBoostInstantSearchRecentDomRecords(record) {
  document.querySelectorAll(BOOST_INSTANT_SEARCH_RECENT_DELETE_SELECTOR).forEach((button) => {
    if (boostInstantSearchRecentRecordsMatch(getBoostInstantSearchRecentRecord(button), record)) {
      button.closest(BOOST_INSTANT_SEARCH_RECENT_ITEM_SELECTOR)?.remove();
    }
  });

  document.querySelectorAll(".boost-sd__suggestion-queries-group:has(> .boost-sd__suggestion-queries-header--recent)")
    .forEach((group) => {
      if (!group.querySelector(BOOST_INSTANT_SEARCH_RECENT_ITEM_SELECTOR)) {
        group.remove();
      }
    });
}

function handleBoostInstantSearchRecentDelete(button) {
  const record = getBoostInstantSearchRecentRecord(button);

  if (!record.title) {
    return;
  }

  const focusTarget = getBoostInstantSearchRecentFocusTarget(button);

  try {
    removeBoostInstantSearchRecentStorageRecord(record);
    removeBoostInstantSearchRecentDomRecords(record);
    announceBoostInstantSearchRecentResult("status-announcement", formatBoostInstantSearchRecentString(
      record.title,
      `Recent search "${BOOST_INSTANT_SEARCH_RECENT_TITLE_PLACEHOLDER}" removed.`
    ));

    window.requestAnimationFrame(() => {
      if (focusTarget?.isConnected) {
        focusTarget.focus({ preventScroll: true });
      }
    });
  } catch (error) {
    console.error("[OOW extension] Boost recent search removal failed", error);
    announceBoostInstantSearchRecentResult(
      "error-announcement",
      "Could not remove the recent search. Please try again."
    );
  }
}

function enhanceBoostInstantSearchResults(results) {
  const state = getBoostInstantSearchState(results);
  const layout = results.querySelector(":scope > .boost-sd__instant-search-autocomplete");

  if (!layout || state === "loading") {
    return;
  }

  enhanceBoostInstantSearchRecentSearches(results);

  const hasCurrentEnhancement = results.dataset.oowBoostIswState === state
    && (state !== "results" || results.querySelector("[data-oow-boost-isw-tablist]"))
    && (state !== "no-results" || layout.firstElementChild?.matches(".boost-sd__no-search-result"));

  if (hasCurrentEnhancement) {
    return;
  }

  results.dataset.oowBoostIswState = state;

  if (state === "empty") {
    return;
  }

  if (state === "no-results") {
    return;
  }

  const otherColumn = layout.querySelector(":scope > .boost-sd__instant-search-column--others");
  const productColumn = layout.querySelector(":scope > .boost-sd__instant-search-column--products");

  if (!otherColumn && !productColumn) {
    return;
  }

  const productPanel = productColumn?.querySelector(".boost-sd__instant-search-group")
    || otherColumn?.querySelector(":scope > .boost-sd__instant-search-product-list");
  const productHeading = productPanel?.querySelector(".boost-sd__instant-search-product-list-header");
  const hasProductResults = productPanel?.querySelector(".boost-sd__suggestion-queries-item--product");
  const collectionPanel = getBoostInstantSearchSuggestionPanel(layout, "collections");
  const pagePanel = getBoostInstantSearchSuggestionPanel(layout, "pages");

  const suffix = results.closest(".boost-sd__search-bar-wrapper--mobile") ? "mobile" : "desktop";
  const panels = [
    ["products", hasProductResults ? productHeading?.textContent.trim() : null, hasProductResults ? productPanel : null],
    ["collections", collectionPanel?.querySelector(".boost-sd__suggestion-queries-header")?.textContent.trim(), collectionPanel],
    ["pages", pagePanel?.querySelector(".boost-sd__suggestion-queries-header")?.textContent.trim(), pagePanel]
  ].filter(([, label, panel]) => label && panel);

  if (!panels.length) {
    return;
  }

  const productHost = productColumn || document.createElement("div");
  const tablist = document.createElement("div");

  if (!productColumn) {
    productHost.className = "boost-sd__instant-search-column boost-sd__instant-search-column--products boost-sd__g-scrollable";
    layout.append(productHost);
  }

  tablist.className = "oow-boost-isw__tabs";
  tablist.dataset.oowBoostIswTablist = "";
  tablist.setAttribute("role", "tablist");

  panels.forEach(([type, label, panel], index) => {
    tablist.append(createBoostInstantSearchTab(type, label, panel, suffix, index === 0));
  });

  productHost.append(tablist);
  panels.forEach(([, , panel]) => productHost.append(panel));

  if (!getBoostInstantSearchSuggestionPanel(layout, "suggestions")) {
    const suggestionHost = otherColumn || document.createElement("div");
    const message = document.createElement("p");

    if (!otherColumn) {
      suggestionHost.className = "boost-sd__instant-search-column boost-sd__instant-search-column--others boost-sd__g-scrollable";
      layout.prepend(suggestionHost);
    }

    message.className = "oow-boost-isw__no-suggestions";
    message.setAttribute("role", "status");
    message.textContent = window.oowTheme?.search?.noMoreSuggestions || "No More Suggestion";
    suggestionHost.prepend(message);
  }
}

function enhanceBoostInstantSearchContent() {
  boostInstantSearchContentFrame = null;
  resetBoostInstantSearchQueryAfterOpen();
  syncBoostInstantSearchPlaceholders();
  document.querySelectorAll(":is(.boost-sd__search-bar-wrapper--desktop, .boost-sd__search-bar-wrapper--mobile) > .boost-sd__search-bar")
    .forEach(enhanceBoostInstantSearchSearchBar);
  document.querySelectorAll(BOOST_INSTANT_SEARCH_RESULT_SELECTOR).forEach(enhanceBoostInstantSearchResults);
}

function scheduleBoostInstantSearchContentEnhancement() {
  if (boostInstantSearchContentFrame !== null) {
    return;
  }

  boostInstantSearchContentFrame = window.requestAnimationFrame(enhanceBoostInstantSearchContent);
}

function mutationUpdatesBoostInstantSearch(mutation) {
  const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
  const targetInput = target?.matches?.(BOOST_INSTANT_SEARCH_INPUT_SELECTOR) ? target : null;

  if (mutation.type === "attributes"
    && mutation.attributeName === "lang"
    && mutation.target === document.documentElement) {
    return true;
  }

  return targetInput && mutation.type === "attributes"
    ? !targetInput.getAttribute("placeholder")?.trim()
      || targetInput.getAttribute("placeholder") === BOOST_INSTANT_SEARCH_DEFAULT_PLACEHOLDER
    : target?.closest?.(".boost-sd__search-bar-wrapper--desktop, .boost-sd__search-bar-wrapper--mobile")
    || Array.from(mutation.addedNodes).some((node) => {
      return node instanceof Element
        && (
          node.matches(BOOST_INSTANT_SEARCH_RESULT_SELECTOR)
          || node.querySelector(BOOST_INSTANT_SEARCH_RESULT_SELECTOR)
          || node.matches(BOOST_INSTANT_SEARCH_INPUT_SELECTOR)
          || node.querySelector(BOOST_INSTANT_SEARCH_INPUT_SELECTOR)
        );
    });
}

function initializeBoostInstantSearchContent() {
  scheduleBoostInstantSearchContentEnhancement();

  if (boostInstantSearchContentObserver) {
    return;
  }

  boostInstantSearchContentObserver = new MutationObserver((mutations) => {
    if (mutations.some(mutationUpdatesBoostInstantSearch)) {
      scheduleBoostInstantSearchContentEnhancement();
    }
  });
  boostInstantSearchContentObserver.observe(document.documentElement, {
    attributeFilter: ["lang", "placeholder"],
    attributes: true,
    childList: true,
    subtree: true
  });
}

const syncBoostInstantSearchPositionThrottled = throttle(syncBoostInstantSearchPosition);

function closeActiveHeaderDialogs() {
  const dialogs = [
    document.querySelector("oow-header-menu[open]"),
    document.querySelector("x-header header-search[open]")
  ].filter(Boolean);

  document.querySelector("x-header.header")?.removeAttribute("data-oow-force-transparent");
  dialogs.forEach((dialog) => dialog.hide());
}

function activateBoostInstantSearch(trigger) {
  if (boostInstantSearchActiveTrigger === trigger) {
    syncBoostInstantSearchPosition();
    return;
  }

  if (boostInstantSearchActiveTrigger) {
    restoreBoostInstantSearchTrigger(false);
  }

  boostInstantSearchActiveTrigger = trigger;
  boostInstantSearchTriggerState = boostInstantSearchPendingTrigger?.trigger === trigger
    ? boostInstantSearchPendingTrigger.state
    : {
      ariaControls: trigger.getAttribute("aria-controls"),
      ariaExpanded: trigger.getAttribute("aria-expanded")
    };
  boostInstantSearchPendingTrigger = null;

  prepareBoostInstantSearchInitialQuery();
  closeActiveHeaderDialogs();
  trigger.removeAttribute("aria-controls");
  trigger.setAttribute("aria-expanded", "true");
  document.documentElement.setAttribute("data-oow-boost-isw-open", "");
  setBoostInstantSearchAnimationState(BOOST_INSTANT_SEARCH_ANIMATION_OPENING);
  syncBoostInstantSearchPosition();
  const header = document.querySelector("x-header.header");
  if (header) {
    boostInstantSearchHeaderObserver = new ResizeObserver(syncBoostInstantSearchPosition);
    boostInstantSearchHeaderObserver.observe(header, { box: "border-box" });
  }
  scheduleBoostInstantSearchContentEnhancement();
  window.requestAnimationFrame(focusBoostInstantSearchInput);
  window.setTimeout(focusBoostInstantSearchInput, 800);
}

function restoreBoostInstantSearchTrigger(restoreFocus = true) {
  const trigger = boostInstantSearchActiveTrigger;
  const triggerState = boostInstantSearchTriggerState;

  window.clearTimeout(boostInstantSearchOpenTimer);
  boostInstantSearchOpenTimer = null;
  clearBoostInstantSearchAnimationFrame();
  clearBoostInstantSearchCloseTimer();
  boostInstantSearchHeaderObserver?.disconnect();
  boostInstantSearchHeaderObserver = null;
  boostInstantSearchActiveTrigger = null;
  boostInstantSearchTriggerState = null;
  document.documentElement.removeAttribute("data-oow-boost-isw-open");
  setBoostInstantSearchAnimationState(null);
  document.documentElement.style.removeProperty("--oow-boost-isw-top");

  if (!trigger || !triggerState) {
    return;
  }

  if (triggerState.ariaControls === null) {
    trigger.removeAttribute("aria-controls");
  } else {
    trigger.setAttribute("aria-controls", triggerState.ariaControls);
  }

  if (triggerState.ariaExpanded === null) {
    trigger.removeAttribute("aria-expanded");
  } else {
    trigger.setAttribute("aria-expanded", triggerState.ariaExpanded);
  }

  if (restoreFocus && trigger.isConnected) {
    window.requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
  }
}

function dispatchBoostInstantSearchClose({ restoreFocus = true } = {}) {
  boostInstantSearchPendingTrigger = null;
  boostInstantSearchRestoreFocusOnClose = restoreFocus;

  if (!boostInstantSearchActiveTrigger) {
    window.dispatchEvent(new CustomEvent(BOOST_INSTANT_SEARCH_CLOSE_EVENT));
    return;
  }

  if (isBoostInstantSearchClosing()) {
    return;
  }

  window.clearTimeout(boostInstantSearchOpenTimer);
  boostInstantSearchOpenTimer = null;
  clearBoostInstantSearchAnimationFrame();
  setBoostInstantSearchAnimationState(BOOST_INSTANT_SEARCH_ANIMATION_CLOSING);
  scheduleBoostInstantSearchClose({ dispatchCloseEvent: true });
}

function restoreBoostInstantSearchAfterClose() {
  const restoreFocus = boostInstantSearchRestoreFocusOnClose;

  // Keep Boost input handlers dormant until the next confirmed opening.
  getBoostInstantSearchQueryInputs().forEach((input) => { input.value = ""; });
  boostInstantSearchPendingQueryReset = true;
  boostInstantSearchRestoreFocusOnClose = true;
  restoreBoostInstantSearchTrigger(restoreFocus);
}

function dispatchBoostInstantSearchOpen(trigger) {
  activateBoostInstantSearch(trigger);

  try {
    window.dispatchEvent(new CustomEvent(BOOST_INSTANT_SEARCH_OPEN_EVENT, {
      detail: { input: getBoostInstantSearchInput(trigger) }
    }));
  } catch (error) {
    console.error("[OOW extension] Boost instant search failed", error);
    restoreBoostInstantSearchTrigger();
    return;
  }

  boostInstantSearchOpenTimer = window.setTimeout(() => {
    boostInstantSearchOpenTimer = null;

    if (boostInstantSearchActiveTrigger === trigger && !isBoostInstantSearchOpen()) {
      restoreBoostInstantSearchTrigger();
    }
  }, 500);
}

function getHeaderSearchTrigger(input) {
  const headerSearch = input?.closest?.("x-header header-search");

  if (!headerSearch?.id) {
    return null;
  }

  return document.querySelector(`${BOOST_INSTANT_SEARCH_TRIGGER_SELECTOR}[aria-controls="${CSS.escape(headerSearch.id)}"]`);
}

function handleBoostInstantSearchOpened(event) {
  boostInstantSearchEmbedEnabled = true;

  if (isBoostInstantSearchClosing()) {
    return;
  }

  const trigger = boostInstantSearchActiveTrigger || getHeaderSearchTrigger(event.detail?.input);

  if (!trigger) {
    return;
  }

  window.clearTimeout(boostInstantSearchOpenTimer);
  boostInstantSearchOpenTimer = null;
  activateBoostInstantSearch(trigger);
  scheduleBoostInstantSearchOpenAnimation();
}

function handleBoostInstantSearchClosed() {
  if (!boostInstantSearchActiveTrigger) {
    return;
  }

  if (boostInstantSearchCloseDispatching) {
    restoreBoostInstantSearchAfterClose();
    return;
  }

  if (!isBoostInstantSearchClosing()) {
    clearBoostInstantSearchAnimationFrame();
    setBoostInstantSearchAnimationState(BOOST_INSTANT_SEARCH_ANIMATION_CLOSING);
    scheduleBoostInstantSearchClose();
  }
}

function syncBoostInstantSearchRuntimeState() {
  if (document.body.classList.contains(BOOST_INSTANT_SEARCH_READY_CLASS)) {
    boostInstantSearchEmbedEnabled = true;
    replayBoostInstantSearchPendingInput();

    const pendingTrigger = boostInstantSearchPendingTrigger?.trigger;

    if (pendingTrigger) {
      if (pendingTrigger.isConnected && pendingTrigger.matches(BOOST_INSTANT_SEARCH_TRIGGER_SELECTOR) && isBoostInstantSearchReady()) {
        dispatchBoostInstantSearchOpen(pendingTrigger);
      } else {
        boostInstantSearchPendingTrigger = null;
      }
    }
  }

  if (isBoostInstantSearchOpen()) {
    if (!boostInstantSearchActiveTrigger) {
      const input = document.querySelector(`x-header header-search[open] ${BOOST_INSTANT_SEARCH_INPUT_SELECTOR}`);
      const trigger = getHeaderSearchTrigger(input);

      if (trigger) {
        activateBoostInstantSearch(trigger);
      }
    }

    if (getBoostInstantSearchAnimationState() === BOOST_INSTANT_SEARCH_ANIMATION_OPENING) {
      scheduleBoostInstantSearchOpenAnimation();
    }

    return;
  }

  if (boostInstantSearchActiveTrigger && boostInstantSearchOpenTimer === null && !isBoostInstantSearchClosing()) {
    clearBoostInstantSearchAnimationFrame();
    setBoostInstantSearchAnimationState(BOOST_INSTANT_SEARCH_ANIMATION_CLOSING);
    scheduleBoostInstantSearchClose();
  }
}

function getHeaderDropdownTrigger(target) {
  return target?.closest?.(HEADER_DROPDOWN_TRIGGER_SELECTOR) || null;
}

function isHeaderDropdownHoverTrigger(trigger) {
  if (!trigger || !window.matchMedia("screen and (pointer: fine)").matches) {
    return false;
  }

  const disclosure = trigger.closest("mega-menu-disclosure, dropdown-menu-disclosure");
  const configuredTrigger = trigger.dataset.oowMenuOpenTrigger || disclosure?.getAttribute("trigger");

  return configuredTrigger === "hover";
}

function handleBoostInstantSearchMouseOver(event) {
  if (!boostInstantSearchActiveTrigger && !boostInstantSearchPendingTrigger) {
    return;
  }

  const trigger = getHeaderDropdownTrigger(event.target);
  const enteredTrigger = !(
    event.relatedTarget instanceof Node
    && trigger?.contains(event.relatedTarget)
  );

  if (enteredTrigger && isHeaderDropdownHoverTrigger(trigger)) {
    dispatchBoostInstantSearchClose({ restoreFocus: false });
  }
}

function isBoostInstantSearchOverlayClick(event) {
  if (!boostInstantSearchActiveTrigger || !window.matchMedia(BOOST_INSTANT_SEARCH_DESKTOP_QUERY).matches) {
    return false;
  }

  const wrapper = event.target.closest?.(BOOST_INSTANT_SEARCH_DESKTOP_WRAPPER_SELECTOR);

  if (!wrapper || event.target !== wrapper) {
    return false;
  }

  const wrapperTop = wrapper.getBoundingClientRect().top;
  const panelBottom = Math.max(wrapperTop, wrapper.querySelector(".boost-sd__search-bar-autocomplete")?.getBoundingClientRect().bottom ?? wrapperTop);

  return event.clientY >= panelBottom;
}

function handleBoostInstantSearchClick(event) {
  const closeButton = event.composedPath().find((node) => {
    return node instanceof Element && node.matches(BOOST_INSTANT_SEARCH_CLOSE_SELECTOR);
  });

  if (closeButton && boostInstantSearchActiveTrigger) {
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    dispatchBoostInstantSearchClose();
    return;
  }

  const recentDelete = event.target.closest?.(BOOST_INSTANT_SEARCH_RECENT_DELETE_SELECTOR);

  if (recentDelete) {
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    handleBoostInstantSearchRecentDelete(recentDelete);
    return;
  }

  const submit = event.composedPath().find((node) => {
    return node instanceof Element && node.matches(BOOST_INSTANT_SEARCH_SEARCH_BUTTON_SELECTOR);
  });
  const submitWrapper = submit?.closest(".boost-sd__search-bar-wrapper--desktop, .boost-sd__search-bar-wrapper--mobile");

  if (submitWrapper) {
    const form = submit.closest("form");
    const query = form?.querySelector(BOOST_INSTANT_SEARCH_INPUT_SELECTOR)?.value.trim();

    if (query) {
      const searchUrl = new URL(form.getAttribute("action") || "/search", window.location.origin);

      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      searchUrl.searchParams.set("q", query);
      window.location.assign(searchUrl.toString());
    }

    return;
  }

  const tab = event.target.closest?.("[data-oow-boost-isw-tab]");

  if (tab) {
    event.preventDefault();
    event.stopPropagation();
    activateBoostInstantSearchTab(tab);
    return;
  }

  const trigger = event.target.closest?.(BOOST_INSTANT_SEARCH_TRIGGER_SELECTOR);

  if (trigger) {
    event.preventDefault();
    event.stopPropagation();

    if (boostInstantSearchPendingTrigger?.trigger === trigger) {
      boostInstantSearchPendingTrigger = null;
      return;
    }

    if (!isBoostInstantSearchReady()) {
      boostInstantSearchPendingTrigger = {
        trigger,
        state: {
          ariaControls: trigger.getAttribute("aria-controls"),
          ariaExpanded: trigger.getAttribute("aria-expanded")
        }
      };

      return;
    }

    if (boostInstantSearchActiveTrigger || isBoostInstantSearchOpen()) {
      if (isBoostInstantSearchClosing()) {
        if (!cancelBoostInstantSearchClose()) {
          restoreBoostInstantSearchTrigger(false);
          dispatchBoostInstantSearchOpen(trigger);
        }
      } else {
        dispatchBoostInstantSearchClose();
      }
    } else {
      dispatchBoostInstantSearchOpen(trigger);
    }

    return;
  }

  if ((boostInstantSearchActiveTrigger || boostInstantSearchPendingTrigger) && (
    event.target.closest?.(HEADER_ACTION_SELECTOR)
    || getHeaderDropdownTrigger(event.target)
  )) {
    dispatchBoostInstantSearchClose({ restoreFocus: false });
    return;
  }

  if (isBoostInstantSearchOverlayClick(event)) {
    event.preventDefault();
    event.stopPropagation();
    dispatchBoostInstantSearchClose();
  }
}

function handleBoostInstantSearchKeydown(event) {
  if ((boostInstantSearchActiveTrigger || boostInstantSearchPendingTrigger) && ["Escape", "Esc"].includes(event.key)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    dispatchBoostInstantSearchClose();
    return;
  }

  const tab = event.target.closest?.("[data-oow-boost-isw-tab]");

  if (tab && ["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
    const tabs = Array.from(tab.closest("[data-oow-boost-isw-tablist]").querySelectorAll("[data-oow-boost-isw-tab]"));
    const currentIndex = tabs.indexOf(tab);
    const targetIndex = event.key === "Home"
      ? 0
      : event.key === "End"
        ? tabs.length - 1
        : (currentIndex + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;

    event.preventDefault();
    event.stopPropagation();
    activateBoostInstantSearchTab(tabs[targetIndex]);
    tabs[targetIndex].focus({ preventScroll: true });
    return;
  }

  const trigger = event.target.closest?.(BOOST_INSTANT_SEARCH_TRIGGER_SELECTOR);

  if (!trigger || event.key !== "Enter") {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  trigger.click();
}

function initializeBoostInstantSearch() {
  boostInstantSearchEmbedEnabled ||= window.boostWidgetIntegration?.taeSettings?.instantSearch?.enabled === true;

  if (boostInstantSearchInitialized) {
    return;
  }

  boostInstantSearchPendingUrlQueryReset = window.themeVariables?.settings?.pageType === "search"
    && !!new URLSearchParams(window.location.search).get("q")?.trim();
  window.addEventListener("input", handleBoostInstantSearchQueryInput, true);
  window.addEventListener("click", handleBoostInstantSearchClick, true);
  window.addEventListener("keydown", handleBoostInstantSearchKeydown, true);
  window.addEventListener("mouseover", handleBoostInstantSearchMouseOver, true);
  window.addEventListener("resize", syncBoostInstantSearchPositionThrottled, { passive: true });
  window.addEventListener("scroll", syncBoostInstantSearchPositionThrottled, { passive: true });
  window.matchMedia(BOOST_INSTANT_SEARCH_DESKTOP_QUERY).addEventListener("change", handleBoostInstantSearchMotionChange);
  window.matchMedia(BOOST_INSTANT_SEARCH_REDUCED_MOTION_QUERY).addEventListener("change", handleBoostInstantSearchMotionChange);
  document.addEventListener(BOOST_INSTANT_SEARCH_OPEN_EVENT, handleBoostInstantSearchOpened);
  document.addEventListener(BOOST_INSTANT_SEARCH_CLOSE_EVENT, handleBoostInstantSearchClosed);
  window.addEventListener(BOOST_INSTANT_SEARCH_OPEN_EVENT, handleBoostInstantSearchOpened);
  window.addEventListener(BOOST_INSTANT_SEARCH_CLOSE_EVENT, handleBoostInstantSearchClosed);
  window.addEventListener("boost-sd-isw-rendered", handleBoostInstantSearchRendered);
  initializeBoostInstantSearchContent();
  boostInstantSearchStateObserver = new MutationObserver(syncBoostInstantSearchRuntimeState);
  boostInstantSearchStateObserver.observe(document.body, {
    attributeFilter: ["class"],
    attributes: true
  });
  boostInstantSearchInitialized = true;
}

function handleProductCardSwatchTooltip(event) {
  const directSwatch = event.target.closest?.(PRODUCT_CARD_SWATCH_TOOLTIP_SELECTOR);
  const swatchControl = event.target.closest?.(PRODUCT_CARD_SWATCH_CONTROL_SELECTOR);
  const swatch = directSwatch || Array.from(swatchControl?.labels || []).find((label) => label.matches(PRODUCT_CARD_SWATCH_TOOLTIP_SELECTOR));
  const visibleSwatches = document.querySelectorAll(`${PRODUCT_CARD_SWATCH_TOOLTIP_SELECTOR}.${PRODUCT_CARD_SWATCH_TOOLTIP_VISIBLE_CLASS}`);
  const isMobileProductCard = window.matchMedia(MOBILE_PRODUCT_CARD_QUERY).matches;

  visibleSwatches.forEach((visibleSwatch) => {
    if (!isMobileProductCard || visibleSwatch !== swatch) {
      visibleSwatch.classList.remove(PRODUCT_CARD_SWATCH_TOOLTIP_VISIBLE_CLASS);
    }
  });

  if (!isMobileProductCard) {
    return;
  }

  swatch?.classList.add(PRODUCT_CARD_SWATCH_TOOLTIP_VISIBLE_CLASS);
}

function initializeProductCardSwatchTooltip() {
  if (productCardSwatchTooltipInitialized) {
    return;
  }

  document.addEventListener("click", handleProductCardSwatchTooltip, true);
  productCardSwatchTooltipInitialized = true;
}

function initializeBoostCardHoverMedia() {
  if (boostCardHoverMediaInitialized) return;

  const syncHoverMedia = (event) => {
    if (isMasterKilled() || (event.type === "keydown" && !["Enter", " "].includes(event.key))) return;
    const option = event.target.closest?.(".boost-sd__product-swatch-option");
    if (!option || option.closest(".boost-sd__popup-select-option")) return;
    if (event.type === "mouseenter" && event.target !== option) return;
    if (!option.querySelector('input[data-swatch-type="color"]')) return;
    const card = option.closest(BOOST_PRODUCT_ITEM_SELECTOR);
    if (!card) return;

    // Boost updates both images in its swatch handler; correct the pair after it finishes.
    requestAnimationFrame(() => {
      if (isMasterKilled() || !card.isConnected) return;
      const primary = card.querySelector(".boost-sd__product-image img.boost-sd__product-image-img--main");
      const secondary = card.querySelector(".boost-sd__product-image img.boost-sd__product-image-img--second");
      const filename = primary?.src.split("?")[0].split("/").pop();
      const group = filename?.match(/^(.*)_pure\.[^.]+$/)?.[1];
      if (!group || !secondary) return;

      let product;
      try {
        product = JSON.parse(card.getAttribute("data-product"));
      } catch {
        return;
      }
      const images = product?.images?.filter((image) => {
        const name = image.src?.split("?")[0].split("/").pop();
        return name && name !== filename && name.startsWith(group);
      });
      // The _pure image duplicates the first image in its color group; use the next view.
      const src = (images?.[1] || images?.[0])?.src || primary.src;
      secondary.src = src;
      secondary.srcset = src;
    });
  };

  document.addEventListener("mouseenter", syncHoverMedia, true);
  document.addEventListener("click", syncHoverMedia);
  document.addEventListener("keydown", syncHoverMedia);
  boostCardHoverMediaInitialized = true;
}

function getProductCardColorSelection(card) {
  if (card.matches(PRODUCT_CARD_SELECTOR)) {
    const control = card.querySelector(`${PRODUCT_CARD_SWATCH_CONTROL_SELECTOR}:checked`);
    if (!control) return null;
    return {
      variantId: control.dataset.variantId,
      handle: card.getAttribute("handle"),
      productUrl: control.dataset.productUrl
    };
  }

  const controls = Array.from(card.querySelectorAll('input[data-swatch-type="color"]'))
    .filter((control) => control.checked && !control.closest('.boost-sd__popup-select-option'));
  if (controls.length !== 1) return null;

  try {
    const product = JSON.parse(card.getAttribute("data-product"));
    const variants = typeof product.variants === "string" ? JSON.parse(product.variants) : product.variants;
    const options = product.options_with_values;
    if (!Array.isArray(options) || !Array.isArray(variants)) return null;
    const colorOptions = options.filter((option) => option.name === "color" || option.original_name === "color");
    if (colorOptions.length !== 1) return null;
    const position = Number(colorOptions[0].position) - 1;
    if (!Number.isInteger(position) || position < 0 || position >= options.length) return null;

    // Boost SSR exposes variant titles rather than structured option values.
    // Reject ambiguous delimiters instead of guessing a color/size combination.
    const matches = variants.filter((variant) => {
      const values = variant.options || variant.title?.split(" / ");
      return Array.isArray(values) && values.length === options.length && values[position] === controls[0].value;
    });
    const variant = matches.find((item) => item.available === true) || matches[0];
    if (!variant || !/^\d+$/.test(String(variant.id))) return null;
    return { variantId: String(variant.id), handle: product.handle };
  } catch {
    return null;
  }
}

function syncProductCardColorUrl(card) {
  if (isMasterKilled()) return;
  const selection = getProductCardColorSelection(card);
  if (!selection) return;
  const passColor = getOowTheme().productCard?.passColorToUrl !== false;
  const links = card.matches(PRODUCT_CARD_SELECTOR)
    ? card.querySelectorAll('a.product-card__media, a.product-title, a.product-card__swatch-overflow')
    : card.querySelectorAll('a.boost-sd__product-link, a.boost-sd__product-swatch-more, .boost-sd__product-swatch-more a');

  const synchronizedLinks = new Set();
  links.forEach((link) => {
    const href = link.getAttribute("href");
    if (!href) return;
    const url = new URL(href, document.baseURI);
    const handle = url.pathname.match(/\/products\/([^/]+)\/?$/)?.[1];
    if (!handle) return;
    // A combined listing can navigate to a different product; its variant IDs are unrelated.
    if (!selection.productUrl && decodeURIComponent(handle) !== selection.handle) return;
    if (passColor) {
      if (decodeURIComponent(handle) !== selection.handle || !/^\d+$/.test(selection.variantId || "")) return;
      url.searchParams.set("variant", selection.variantId);
    } else {
      url.searchParams.delete("variant");
    }
    link.setAttribute("href", url.href);
    synchronizedLinks.add(link);
  });
  return synchronizedLinks;
}

function initializeProductCardColorUrl() {
  if (productCardColorUrlInitialized) {
    return;
  }

  const cardSelector = `${PRODUCT_CARD_SELECTOR}, ${BOOST_PRODUCT_ITEM_SELECTOR}`;
  document.addEventListener("change", (event) => {
    const control = event.target.closest?.(`${PRODUCT_CARD_SWATCH_CONTROL_SELECTOR}, input[data-swatch-type="color"]`);
    const card = control?.closest(cardSelector);
    if (!card || control.closest('.boost-sd__popup-select-option')) return;
    queueMicrotask(() => syncProductCardColorUrl(card));
  }, true);

  // Read the current selection before native or Boost navigation consumes the link.
  ["click", "auxclick", "contextmenu"].forEach((type) => {
    document.addEventListener(type, (event) => {
      const link = event.target.closest?.("a[href]");
      const card = link?.closest(cardSelector);
      if (!card) return;
      const links = syncProductCardColorUrl(card);
      if (type === "click" && card.matches(BOOST_PRODUCT_ITEM_SELECTOR) && links?.has(link)) {
        // Boost rebuilds the destination without the selected variant. Keep native anchor navigation.
        event.stopImmediatePropagation();
      }
    }, true);
  });

  productCardColorUrlInitialized = true;
}

class OowHeaderMenu extends DialogElement {
  get shouldLock() {
    return true;
  }

  connectedCallback() {
    super.connectedCallback();

    this._activeKey = null;
    this._lastTrigger = null;
    this._openedByHover = false;
    this._hoverCloseTimer = null;
    this._menuScrollEndTimer = null;
    this._restoreTransparentAtTop = false;
    this._scrollingMenuArea = null;
    this.hydrateMenuGroups();
    this._handleResize = throttle(() => {
      if (this.open) {
        this.syncPosition();
      }
    });
    this._handleScroll = throttle(() => {
      this.syncTransparentState();
    });
    this._resizeObserver = new ResizeObserver(this._handleResize);
    const header = document.querySelector("x-header.header");

    if (header) {
      this._resizeObserver.observe(header);
    }

    const announcementBar = document.querySelector(".announcement-bar");

    if (announcementBar) {
      this._resizeObserver.observe(announcementBar);
    }

    window.addEventListener("resize", this._handleResize, { signal: this.abortController.signal });
    window.addEventListener("scroll", this._handleScroll, { signal: this.abortController.signal, passive: true });
    this.querySelectorAll(".oow-header-menu__panels, .oow-header-menu__groups").forEach((scrollArea) => {
      scrollArea.addEventListener("scroll", () => this.handleMenuScroll(scrollArea), {
        passive: true,
        signal: this.abortController.signal
      });
    });

    if (header) {
      header.addEventListener("pointerleave", () => {
        if (window.matchMedia("screen and (pointer: fine)").matches) {
          this.clearForceTransparent();
        }
      }, { signal: this.abortController.signal });

      header.querySelector("header-search")?.addEventListener("dialog:before-show", () => {
        this.clearForceTransparent();
      }, { signal: this.abortController.signal });
    }

    this.addEventListener("dialog:before-show", () => this.syncHeaderState(true), { signal: this.abortController.signal });
    this.addEventListener("dialog:before-hide", () => this.syncHeaderState(false), { signal: this.abortController.signal });
    this.addEventListener("dialog:after-hide", () => {
      this._lastTrigger?.focus({ preventScroll: true });
      this._lastTrigger = null;
    }, { signal: this.abortController.signal });

    document.addEventListener("click", this.handleDocumentClick.bind(this), { capture: true, signal: this.abortController.signal });
    document.addEventListener("mouseover", this.handleDocumentMouseOver.bind(this), { signal: this.abortController.signal });
    document.addEventListener("mouseout", this.handleDocumentMouseOut.bind(this), { signal: this.abortController.signal });

    if (window.Shopify?.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.handleThemeBlockSelect(event), {
        signal: this.abortController.signal
      });
    }

    this.updatePanel(this.getDefaultKey());
  }

  disconnectedCallback() {
    this.cancelHoverClose();
    this.clearMenuScrollState();
    this.clearMenuAppearances();
    this._resizeObserver?.disconnect();
    this._handleResize?.cancel();
    this._handleScroll?.cancel();
    super.disconnectedCallback();
  }

  handleMenuScroll(scrollArea) {
    if (this._scrollingMenuArea !== scrollArea) {
      this._scrollingMenuArea?.removeAttribute("data-oow-scrolling");
      this._scrollingMenuArea = scrollArea;
    }

    window.clearTimeout(this._menuScrollEndTimer);
    scrollArea.setAttribute("data-oow-scrolling", "");
    this._menuScrollEndTimer = window.setTimeout(() => this.clearMenuScrollState(), 500);
  }

  clearMenuScrollState() {
    window.clearTimeout(this._menuScrollEndTimer);
    this._menuScrollEndTimer = null;
    this._scrollingMenuArea?.removeAttribute("data-oow-scrolling");
    this._scrollingMenuArea = null;
  }

  hydrateMenuGroups() {
    const configuredMenuKeys = new Set();

    this.clearMenuAppearances();

    this.querySelectorAll("[data-oow-menu-group]").forEach((group) => {
      const menuKey = group.dataset.oowMenuGroup;

      if (!menuKey || configuredMenuKeys.has(menuKey)) {
        return;
      }

      configuredMenuKeys.add(menuKey);
      const panel = this.querySelector(`[data-menu-panel="${CSS.escape(menuKey)}"]`);

      group.querySelectorAll("[data-oow-menu-appearance]").forEach((appearance) => {
        this.hydrateMenuAppearance(appearance, menuKey, panel);
      });

      if (!panel) {
        return;
      }

      const mediaSlot = panel.querySelector("[data-oow-menu-media-slot]");
      const mediaCards = group.querySelectorAll("[data-oow-menu-content]:not([hidden])");

      if (!mediaSlot || mediaCards.length === 0) {
        return;
      }

      mediaCards.forEach((mediaCard) => {
        mediaCard.dataset.oowMenuOwner = menuKey;
        mediaSlot.append(mediaCard);
      });
      mediaSlot.hidden = false;
    });

    // 媒体卡片刚被搬进面板，此时数量才最终确定，需要同步一次。
    this.syncMenuMediaCount();
  }

  /*
    统计当前激活 tab 面板里的媒体卡片数量，写到 data-oow-menu-media-count 上。
    CSS 靠这个属性区分 0 张 / 1 张 / 2 张的情况来切换栅格列宽：
    没有媒体图时不该再给右侧预留两列空白，只有 1 张时列宽也要相应收窄。
  */
  syncMenuMediaCount() {
    const key = this._activeKey || this.getDefaultKey();
    const panel = key ? this.querySelector(`[data-menu-panel="${CSS.escape(key)}"]`) : null;
    const mediaCount = panel?.querySelector("[data-oow-menu-media-slot]")?.querySelectorAll("[data-oow-menu-content]").length || 0;

    this.dataset.oowMenuMediaCount = String(mediaCount);
  }

  clearMenuAppearances() {
    const roots = [document.querySelector("x-header.header"), this].filter(Boolean);

    roots.forEach((root) => {
      root.querySelectorAll("[data-oow-generated-badge]").forEach((badge) => badge.remove());
      root.querySelectorAll("[data-oow-menu-decorated]").forEach((target) => {
        target.style.removeProperty("--oow-menu-item-color");
        target.removeAttribute("data-oow-menu-decorated");
      });
    });
  }

  hydrateMenuAppearance(appearance, menuKey, panel) {
    const targetScope = appearance.dataset.oowMenuTargetScope;
    const badgeText = appearance.dataset.oowMenuBadgeText?.trim();
    const useCustomColor = appearance.dataset.oowMenuCustomColor === "true";
    const textColor = appearance.dataset.oowMenuTextColor?.trim();

    if (!badgeText && (!useCustomColor || !textColor)) {
      return;
    }

    let targets = [];

    if (targetScope === "current_group") {
      const selector = `[data-oow-menu-level="1"][data-oow-menu-key="${CSS.escape(menuKey)}"]`;
      const roots = [document.querySelector("x-header.header"), this].filter(Boolean);
      targets = roots.flatMap((root) => Array.from(root.querySelectorAll(selector)));
    } else if (targetScope === "descendant" && panel) {
      const targetPath = this.normalizeMenuPath(appearance.dataset.oowMenuTargetPath);

      if (!targetPath) {
        return;
      }

      targets = Array.from(panel.querySelectorAll('[data-oow-menu-level="2"], [data-oow-menu-level="3"]')).filter((target) => {
        const menuPath = this.normalizeMenuPath(target.dataset.oowMenuPath);
        const menuPathSegments = menuPath.split(" > ");
        const pathMatches = menuPath === targetPath
          || (!targetPath.includes(" > ") && menuPathSegments[menuPathSegments.length - 1] === targetPath);

        return pathMatches;
      });
    }

    targets.forEach((target) => {
      target.querySelectorAll(":scope > [data-oow-generated-badge]").forEach((badge) => badge.remove());
      target.style.removeProperty("--oow-menu-item-color");
      target.setAttribute("data-oow-menu-decorated", "");

      if (useCustomColor && textColor) {
        target.style.setProperty("--oow-menu-item-color", textColor);
      }

      if (badgeText) {
        const badge = document.createElement("span");
        badge.className = "oow-header-menu__badge";
        badge.dataset.oowGeneratedBadge = "";
        badge.textContent = badgeText;
        target.append(badge);
      }
    });
  }

  normalizeMenuUrl(value) {
    const normalizedValue = value?.trim();

    if (!normalizedValue) {
      return "";
    }

    try {
      const url = new URL(normalizedValue, window.location.origin);
      const pathname = url.pathname.length > 1 ? url.pathname.replace(/\/$/, "") : url.pathname;

      return `${pathname}${url.search}`.toLowerCase();
    } catch (error) {
      return normalizedValue.toLowerCase();
    }
  }

  normalizeMenuPath(value) {
    return value?.trim().toLowerCase().replace(/\s*>\s*/g, " > ").replace(/\s+/g, " ") || "";
  }

  handleThemeBlockSelect(event) {
    const selectedElement = event.target.closest?.("[data-oow-menu-owner], [data-oow-menu-group]");
    const menuKey = selectedElement?.dataset.oowMenuOwner || selectedElement?.dataset.oowMenuGroup;

    if (menuKey) {
      this.updatePanel(menuKey);
    }
  }

  getDefaultKey() {
    const configuredKey = this.dataset.defaultMenu;
    const firstPanel = this.querySelector("[data-menu-panel]");

    return configuredKey && this.querySelector(`[data-menu-panel="${CSS.escape(configuredKey)}"]`) ? configuredKey : firstPanel?.dataset.menuPanel;
  }

  getHeaderControl(target) {
    const control = target?.closest?.(`[aria-controls="${this.id}"]`);

    return control && (control.dataset.menuKey || control.hasAttribute("data-oow-menu-toggle")) ? control : null;
  }

  getMenuSurface(target) {
    const surface = target?.closest?.(".oow-header-menu__surface");

    return surface && this.contains(surface) ? surface : null;
  }

  isHoverControl(control) {
    return Boolean(control?.dataset.menuKey) && control.dataset.oowMenuOpenTrigger === "hover" && window.matchMedia("screen and (pointer: fine)").matches;
  }

  containsRelatedTarget(element, event) {
    return event.relatedTarget instanceof Node && element.contains(event.relatedTarget);
  }

  handleDocumentMouseOver(event) {
    const control = this.getHeaderControl(event.target);

    if (this.isHoverControl(control) && !this.containsRelatedTarget(control, event)) {
      this.openFromHover(control);
      return;
    }

    const surface = this.getMenuSurface(event.target);

    if (this._openedByHover && surface && !this.containsRelatedTarget(surface, event)) {
      this.cancelHoverClose();
    }
  }

  handleDocumentMouseOut(event) {
    const control = this.getHeaderControl(event.target);

    if (this.isHoverControl(control) && !this.containsRelatedTarget(control, event)) {
      this.scheduleHoverClose();
      return;
    }

    const surface = this.getMenuSurface(event.target);

    if (this._openedByHover && surface && !this.containsRelatedTarget(surface, event)) {
      this.scheduleHoverClose();
    }
  }

  openFromHover(control) {
    const key = control.dataset.menuKey;

    if (!key) {
      return;
    }

    this.cancelHoverClose();
    this.closeActiveHeaderSearch();
    this._lastTrigger = null;
    this._openedByHover = true;

    if (this._activeKey !== key) {
      this.updatePanel(key);
    }

    if (!this.open) {
      this.show();
    }
  }

  scheduleHoverClose() {
    this.cancelHoverClose();
    this._hoverCloseTimer = window.setTimeout(() => {
      this._hoverCloseTimer = null;
      this.hide();
    }, 250);
  }

  cancelHoverClose() {
    window.clearTimeout(this._hoverCloseTimer);
    this._hoverCloseTimer = null;
  }

  closeActiveHeaderSearch() {
    document.querySelector("x-header header-search[open]")?.hide();
  }

  hideForOutsideClickTarget(target) {
    if (this.getHeaderControl(target)) {
      return false;
    }

    return super.hideForOutsideClickTarget(target);
  }

  allowOutsideClickForTarget(target) {
    return Boolean(this.getHeaderControl(target)) || super.allowOutsideClickForTarget(target);
  }

  handleDocumentClick(event) {
    const backdrop = event.target.closest?.("[data-oow-menu-backdrop]");

    if (backdrop && this.contains(backdrop)) {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
      return;
    }

    const control = event.target.closest?.(`[aria-controls="${this.id}"]`);
    const switcher = event.target.closest?.("[data-oow-menu-switcher][data-menu-key]");

    if (switcher && this.contains(switcher)) {
      event.preventDefault();
      event.stopPropagation();
      this.updatePanel(switcher.dataset.menuKey);
      return;
    }

    if (!control) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.cancelHoverClose();
    this._openedByHover = false;
    this.closeActiveHeaderSearch();

    const key = control.dataset.menuKey;
    this._lastTrigger = control;

    if (this.open) {
      if (key && this._activeKey !== key) {
        this.updatePanel(key);
        return;
      }

      if (key) {
        return;
      }

      this.hide();
      return;
    }

    this.updatePanel(key || this.getDefaultKey());
    this.show();
  }

  updatePanel(key) {
    if (!key || !this.querySelector(`[data-menu-panel="${CSS.escape(key)}"]`)) {
      key = this.getDefaultKey();
    }

    if (!key) {
      return;
    }

    this._activeKey = key;

    this.querySelectorAll("[data-menu-panel]").forEach((panel) => {
      panel.hidden = panel.dataset.menuPanel !== key;
    });

    // 切 tab 后可见的媒体卡片变了，刷新计数让 CSS 重新匹配列宽。
    this.syncMenuMediaCount();

    this.querySelectorAll("[data-oow-menu-switcher][data-menu-key]").forEach((switcher) => {
      const expanded = this.open && switcher.dataset.menuKey === key;
      switcher.setAttribute("aria-expanded", String(expanded));
    });

    this.controls.forEach((control) => {
      const isMenuControl = control.dataset.menuKey ? control.dataset.menuKey === key : control.hasAttribute("data-oow-menu-toggle");
      control.setAttribute("aria-expanded", String(this.open && isMenuControl));
      control.toggleAttribute("data-menu-active", this.open && isMenuControl);
    });
  }

  syncHeaderState(open) {
    const header = document.querySelector("x-header.header");

    if (header) {
      header.toggleAttribute("data-oow-menu-open", open);

      if (open) {
        this._restoreTransparentAtTop = false;
        header.removeAttribute("data-oow-force-transparent");
      } else {
        this.cancelHoverClose();
        this._openedByHover = false;
        this._restoreTransparentAtTop = true;
        this.syncTransparentState();
      }
    }

    if (open) {
      this.syncPosition();
    } else {
      this.clearMenuScrollState();
      this.style.removeProperty("--oow-header-menu-top");
    }

    this.updatePanel(this._activeKey || this.getDefaultKey());
  }

  clearForceTransparent() {
    this._restoreTransparentAtTop = false;
    document.querySelector("x-header.header")?.removeAttribute("data-oow-force-transparent");
  }

  syncTransparentState() {
    const header = document.querySelector("x-header.header");

    if (!header) {
      return;
    }

    const shouldForceTransparent = this._restoreTransparentAtTop && !this.open && window.scrollY <= 0;
    header.toggleAttribute("data-oow-force-transparent", shouldForceTransparent);
  }

  syncPosition() {
    const header = document.querySelector("x-header.header");

    if (!header) {
      return;
    }

    const headerBottom = Math.max(0, header.getBoundingClientRect().bottom);
    this.style.setProperty("--oow-header-menu-top", `${headerBottom}px`);
  }
}

if (!window.customElements.get("oow-header-menu")) {
  window.customElements.define("oow-header-menu", OowHeaderMenu);
}

function getSourceDomain() {
  try {
    return window.localStorage.getItem("SOURCE_DOMAIN")?.trim() || "United States";
  } catch {
    return "United States";
  }
}

function initializeSourceDomain(root) {
  const sourceDomain = new URLSearchParams(window.location.search).get("source_domain");

  if (sourceDomain !== null) {
    try {
      window.localStorage.setItem("SOURCE_DOMAIN", sourceDomain);
    } catch {
      // Storage may be unavailable; still try to read a previously saved value.
    }
  }

  const label = getSourceDomain();

  root.querySelectorAll("[data-oow-source-domain]").forEach((element) => {
    element.textContent = label;
  });
}

/*
  <shopify-account> 把头像渲染在自己的 shadow root 里（attachShadow({ mode: "open" })），
  外部 CSS 无法选中 .account-button__avatar-initials，官方也只暴露 signed-out-avatar 这一个
  part，登录后的首字母没有任何样式钩子。
  组件内部给 .account-button__avatar-initials 加了 `text-box: trim-both cap alphabetic`：
  按字体 cap 高度做上下修剪。一旦字体 cap 度量不准，或首字母不是拉丁字符（如中文），
  修剪出来的盒子就不是字形的视觉盒子，首字母会明显偏离圆心。这里把修剪关掉，
  改用 line-height: 1 的行盒居中（对拉丁字母和中文都接近视觉居中）。
*/
const ACCOUNT_AVATAR_INITIALS_STYLE_ID = "oow-account-avatar-initials";

const ACCOUNT_AVATAR_INITIALS_STYLE = `
.account-button__avatar-initials {
  text-box: revert !important;
  line-height: 1 !important;
}
`;

// 向每个 <shopify-account> 的 shadow root 注入覆盖样式；靠 style id 做幂等，避免重复注入。
function patchAccountAvatarInitials(root) {
  root.querySelectorAll("shopify-account").forEach((account) => {
    const shadowRoot = account.shadowRoot;

    if (!shadowRoot || shadowRoot.getElementById(ACCOUNT_AVATAR_INITIALS_STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = ACCOUNT_AVATAR_INITIALS_STYLE_ID;
    style.textContent = ACCOUNT_AVATAR_INITIALS_STYLE;
    shadowRoot.appendChild(style);
  });
}

// 组件可能还没升级完成，此时 shadowRoot 尚不存在，要等 whenDefined 之后再打补丁。
function initializeAccountAvatarInitials(root = document) {
  if (window.customElements.get("shopify-account")) {
    patchAccountAvatarInitials(root);
    return;
  }

  window.customElements.whenDefined("shopify-account").then(() => patchAccountAvatarInitials(root));
}

function initializeExtension(root = document) {
  runSafely("header-scroll-state", initializeHeaderScrollState);
  runSafely("boost-toolbar-sticky", initializeBoostToolbarSticky);
  runSafely("product-mobile-media", () => initializeMobileProductMedia(root));

  if (isMasterKilled()) {
    return;
  }

  runSafely("source-domain", () => initializeSourceDomain(root));
  runSafely("account-avatar-initials", () => initializeAccountAvatarInitials(root));
  runSafely("product-sticky-bar-footer-threshold", initializeProductStickyBarFooterThreshold);

  runSafely("patch-registry", () => {
    if (!patchesApplied) {
      applyOowPatches({ root });
      patchesApplied = true;
    }
  });

  runSafely("header-dismissal", initializeHeaderDismissal);
  runSafely("footer-tlab-localization", () => initializeTlabLocalization(root));
  runSafely("product-currency-codes", initializeProductCurrencyCodes);
  runSafely("boost-instant-search", initializeBoostInstantSearch);
  runSafely("boost-search-title", initializeBoostSearchTitle);
  runSafely("boost-theme-quick-buy", initializeBoostThemeQuickBuy);
  runSafely("product-card-swatch-tooltip", initializeProductCardSwatchTooltip);
  runSafely("boost-card-hover-media", initializeBoostCardHoverMedia);
  runSafely("product-card-color-url", initializeProductCardColorUrl);
  runSafely("product-media-page-top", initializeProductMediaPageTop);
  runSafely("product-info-sticky", initializeProductInfoSticky);
  runSafely("product-size-selector", initializeSizeSelector);

  runSafely("runtime", () => {
    const oowTheme = getOowTheme();
    oowTheme.delegate = oowTheme.delegate || new Delegate(document.body);

    document.documentElement.dispatchEvent(new CustomEvent(OOW_SECTION_READY_EVENT, {
      bubbles: true,
      detail: { root }
    }));
  });
}

const initializeExtensionDebounced = debounce(initializeExtension, 50);

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initializeExtension(document), { once: true });
} else {
  initializeExtension(document);
}

document.addEventListener("shopify:section:load", (event) => {
  initializeExtensionDebounced(event.target || document);
});

document.addEventListener("product:rerender", (event) => {
  if (!event.detail?.productChange) return;
  const section = event.target.closest(".shopify-section--main-product");
  if (section) queueMicrotask(() => {
    runSafely("product-mobile-media", () => initializeMobileProductMedia(section));
    runSafely("product-info-sticky", initializeProductInfoSticky);
  });
}, true);

document.addEventListener("shopify:section:unload", (event) => {
  if (event.target.matches?.('.shopify-section--main-product, .shopify-section--header')) {
    productInfoStickyCleanup?.();
    productInfoStickyCleanup = null;
  }
  if (boostInstantSearchPendingTrigger && event.target.contains(boostInstantSearchPendingTrigger.trigger)) {
    boostInstantSearchPendingTrigger = null;
  }
  if (boostInstantSearchActiveTrigger && event.target.contains(boostInstantSearchActiveTrigger)) {
    window.dispatchEvent(new CustomEvent(BOOST_INSTANT_SEARCH_CLOSE_EVENT));
    restoreBoostInstantSearchAfterClose();
  }
  if (event.target.querySelector?.(".boost-sd__filter-product-list")) {
    boostSearchTitleCleanup?.();
    boostSearchTitleCleanup = null;
  }
  if (event.target.matches?.(".shopify-section--header")) {
    cleanupHeaderResize();
    syncHeaderScrollState.cancel();
    headerScrollElement = null;
  }
  if (event.target.matches?.(".shopify-section--header, .shopify-section--announcement-bar") || event.target.querySelector?.(".boost-sd__filter-product-list")) cleanupBoostToolbarSticky();
  for (const [gallery, cleanup] of mobileProductMediaControllers) {
    if (event.target.contains(gallery)) {
      cleanup();
      mobileProductMediaControllers.delete(gallery);
    }
  }
});

document.documentElement.dispatchEvent(new CustomEvent(OOW_READY_EVENT, {
  bubbles: true,
  detail: { features: getFeatures() }
}));

/* TEMP-DEBUG: 强制 oow-header-menu 常显（调试完需删除，恢复 hover 触发） */
// customElements.whenDefined("oow-header-menu").then(() => {
//   const menu = document.getElementById("oow-header-menu");

//   if (!menu) {
//     return;
//   }

//   menu.scheduleHoverClose = () => {};
//   menu.hide = () => Promise.resolve();
//   menu.updatePanel(menu.getDefaultKey());
//   menu.show();
// });
